import { t } from 'i18next';
import _ from 'lodash';
import moment from 'moment';
import { createElement } from 'react';
import { ensure } from '../../../../../../shared/common/utils';
import { EMenuEntry, TMenuEntry, TMenuEntryItem } from '../../../../../../shared/frontend/components/menu/menuHelper';
import { openGlobalReportModal } from '../../../../../../shared/frontend/components/moderation/GlobalReportModal';
import {
  getCharacterProfileMainSpec,
  getProfileEntityColorClassname,
  getProfileFaction,
  getRecruitmentProfileName
} from '../../../../../../shared/frontend/scripts/recruitment/utils';
import { SpecIcon } from '../../../../../../shared/frontend/scripts/wow/components/SpecIcon';
import { asset } from '../../../../../../shared/frontend/services/assetResolverService';
import { FeatureFlag } from '../../../../../../shared/gameConfig/sharedTypes';
import {
  mdiBfa2020Config,
  mdiDF1Config,
  mdiSL1Config,
  mdiSL2Config,
  mdiSL3Config
} from '../../../../../../shared/gameConfig/wowretail/mdi';
import { RecruitmentActivationStatus, RecruitmentEntityType } from '../../../../../../shared/types/recruitmentTypes';
import { StatisticsDataType } from '../../../../../../shared/types/statisticsTypes';
import { TSubRegionResource } from '../../../../../../shared/types/utilityTypes';
import { isCurrentExpansionRaid } from '../../../../shared/enumHelpers';
import { ALL_KEYSTONE_BRACKETS, RAID_METADATA_BY_SLUG, SEASONS } from '../../../../shared/enums';
import { gameConfig } from '../../../../shared/gameConfig';
import { recruitmentSearchPaths } from '../../../../shared/recruitmentShared';
import { raidSupportsGlobalCoverage, raidSupportsWorldFirstTracker } from '../../../../shared/utils';
import { namedRoutes, resolveRoutePath } from '../../bundles/routes';
import { buildSearchUrlFromProfile } from '../../recruitmentProfiles/components/searchUtils';
import { ACTIVITY_INFO_FOR_TYPE } from '../../shared/recruitment/components/RecruitmentActivityIcon';
import { useRecruitmentProfiles } from '../../shared/recruitment/recruitmentHooks';
import {
  BETA_STATISTICS_GROUPS,
  BETA_STATISTICS_TYPES,
  STATISTIC_GROUPS_METRIC_ICON_SRC_MAP,
  STATISTIC_METRICS_ICONS,
  STATISTIC_TYPE_TO_ROUTE_TYPE_MAP,
  StatisticMetricsMenuItemsGroup,
  STATISTICS_GROUPS_TO_DATA_TYPES_MAP
} from '../../statistics/constants';
import {
  EXPORT_METRICS_VISIBILITY_PARAM_KEY,
  parseMetricsVisibility
} from '../../statistics/hooks/useStatisticMetricsVisibility';
import { buildRouteParams, getCharacterSubMenuItems, getSpecSubMenuItems, getWarbandSubMenuItems } from './menuHelper';
import { WATCH_GUILDS_BY_RAID } from './worldFirstWatchConstants';

const now = moment.utc();
const currentSeason = ensure(
  SEASONS.find((s) => s.isMainSeason && now.isAfter(s.starts.us)),
  'Could not find active season'
);

function undefinedIfEmptyArray(arr: any[]) {
  return arr.length === 0 ? undefined : arr;
}

const TOURNAMENT_REGIONS = [
  {
    name: 'World',
    short_name: 'region.world.short-name',
    slug: 'world'
  },
  {
    name: 'region.americas.name',
    short_name: 'region.americas.short-name',
    slug: 'americas'
  },
  {
    name: 'regions:apac.name',
    short_name: 'regions.apac.short-name',
    slug: 'apac'
  },
  {
    name: 'regions.europe.name',
    short_name: 'regions.europe.short-name',
    slug: 'europe'
  },
  {
    name: 'regions.china.name',
    short_name: 'regions.china.short-name',
    slug: 'china'
  }
];

const TOURNAMENT_REGIONS_NO_CHINA = TOURNAMENT_REGIONS.filter((r) => r.slug !== 'china');

const NEWS_MENU_ITEMS: TMenuEntry[] = [
  {
    type: EMenuEntry.ITEM,
    title: 'nav:news.recent-updates',
    link: '/news',
    wowIcon: 'ability_racial_timeismoney'
  },
  {
    type: EMenuEntry.DIVIDER
  },
  {
    type: EMenuEntry.ITEM,
    title: 'nav:news.esports',
    link: '/news?tag=esports',
    wowIcon: 'inv_misc_soccerball'
  },
  {
    type: EMenuEntry.ITEM,
    title: 'nav:news.interviews',
    link: '/news?tag=interview',
    wowIcon: 'inv_scroll_05'
  },
  {
    type: EMenuEntry.ITEM,
    title: 'nav:news.inside-look',
    link: '/news?tag=inside-look',
    wowIcon: 'inv_helm_glasses_b_01_gold_teal'
  },
  {
    type: EMenuEntry.ITEM,
    title: 'nav:news.lore',
    link: '/news?tag=lore',
    wowIcon: 'ability_racial_ancienthistory'
  }
  // {
  //   type: EMenuEntry.ITEM,
  //   title: 'nav:news.weekly-route',
  //   link: namedRoutes.WeeklyRoutes,
  //   wowIcon: 'inv_misc_paperbundle03a'
  // },
  // {
  //   type: EMenuEntry.ITEM,
  //   title: 'nav:news.play-like-the-pros',
  //   link: resolveRoutePath(namedRoutes.PlayLikeThePros),
  //   wowIcon: 'inv_misc_trophy_argent'
  // },
  // {
  //   type: EMenuEntry.ITEM,
  //   title: 'nav:news.rwf',
  //   link: '/news?tag=rwf',
  //   wowIcon: 'inv_achievement_raiddragon_raid'
  // },
  // {
  //   type: EMenuEntry.ITEM,
  //   title: 'nav:news.events',
  //   link: '/news?tag=events',
  //   wowIcon: 'achievement_worldevent_lunar'
  // },
];

const GUIDES_MENU_ITEMS: TMenuEntry[] = [
  {
    type: EMenuEntry.ITEM,
    title: 'nav:news.mythic-plus-guides',
    wowIcon: 'spell_holy_borrowedtime',
    link: resolveRoutePath(namedRoutes.WeeklyRoutes),
    children: [
      {
        type: EMenuEntry.ITEM,
        title: 'nav:mythic-plus.routes-and-guides.weekly-route',
        link: resolveRoutePath(namedRoutes.WeeklyRoutes),
        wowIcon: 'inv_misc_paperbundle03a'
      },
      {
        type: EMenuEntry.ITEM,
        title: 'nav:mythic-plus.routes-and-guides.dungeons-skips-and-tricks',
        link: 'https://www.youtube.com/playlist?list=PLTakbTpb-EBI3KTW0JhqbO5fpaXWJ45tm',
        wowIcon: 'ability_rogue_sprint',
        postIconClass: 'fa fa-external-link slds-m-left--x-small'
      },
      {
        type: EMenuEntry.ITEM,
        title: 'nav:mythic-plus.routes-and-guides.quick-boss-guides',
        link: 'https://youtube.com/playlist?list=PLTakbTpb-EBLi1NZ8THXLn3VpI1LtjWAn&si=K6528LiLzZzxJNPN',
        wowIcon: 'ability_stealth',
        postIconClass: 'fa fa-external-link slds-m-left--x-small'
      },
      {
        type: EMenuEntry.ITEM,
        title: () => t('nav:learn.expansion-affixes', { expansion: t('common:expansions.midnight.name') }),
        link: '/midnight-affixes',
        wowIcon: 'inv_achievement_raid_darkwell'
      },

      {
        type: EMenuEntry.DIVIDER
      },

      {
        type: EMenuEntry.ITEM,
        title: 'nav:learn.mythic-plus-101',
        wowIcon: 'inv_relics_hourglass_02',
        children: [
          {
            type: EMenuEntry.ITEM,
            title: 'nav:learn.what-is-mythic-plus',
            link: '/news/14-mythic-plus-101-what-is-mythic-plus',
            wowIcon: 'achievement_zone_jadeforest'
          },
          {
            type: EMenuEntry.ITEM,
            title: 'nav:learn.your-first-mplus-run',
            link: '/news/51-mythic-plus-101-what-to-know-for-your-first-keystone',
            wowIcon: 'inv_first_aid_70_medicalkit'
          },
          {
            type: EMenuEntry.ITEM,
            title: 'nav:learn.pugging',
            link: '/news/228-mythic-plus-101-pugging-networking-and-group-dynamics',
            wowIcon: 'ui_embercourt-emoji-elated'
          },
          {
            type: EMenuEntry.ITEM,
            title: 'nav:learn.netiquette',
            link: '/news/237-mythic-plus-101-netiquette',
            wowIcon: 'spell_magic_polymorphrabbit'
          },
          {
            type: EMenuEntry.ITEM,
            title: 'nav:learn.understanding-your-role',
            link: '/news/240-mythic-plus-101-understanding-your-role',
            wowIcon: 'inv_misc_dice_01'
          }
        ]
      },
      {
        type: EMenuEntry.ITEM,
        title: 'nav:learn.how-to-use-the-tournament-realm',
        link: '/news/530-guide-how-to-use-the-tournament-realm ',
        wowIcon: 'inv_misc_mdi_banner03'
      }
    ]
  },
  {
    type: EMenuEntry.ITEM,
    title: 'nav:news.raiding-guides',
    wowIcon: 'inv_achievement_raidemeralddream_raid',
    link: '/news/504-raiding-101-how-to-prep-for-a-new-raid',
    children: [
      {
        type: EMenuEntry.ITEM,
        title: 'nav:learn.raiding-101',
        wowIcon: 'inv_achievement_raiddragon_raid',
        children: [
          {
            type: EMenuEntry.ITEM,
            title: 'nav:learn.prepare-for-new-raid',
            wowIcon: 'inv_toy_booklibrary',
            link: '/news/504-raiding-101-how-to-prep-for-a-new-raid',
            children: [
              {
                type: EMenuEntry.ITEM,
                title: 'nav:learn.raider-tips',
                link: '/news/504-raiding-101-how-to-prep-for-a-new-raid',
                wowIcon: 'achievement_boss_illidan'
              },
              {
                type: EMenuEntry.ITEM,
                title: 'nav:learn.guild-leadership-tips',
                link: '/news/571-raiding-101-preparing-your-guild-for-a-new-tier',
                wowIcon: 'achievement_boss_argus_femaleeredar'
              }
            ]
          },
          {
            type: EMenuEntry.ITEM,
            title: 'nav:learn.guild-recruitment',
            link: '/news/540-raiding-101-guild-recruitment',
            wowIcon: 'inv_misc_groupneedmore'
          },
          {
            type: EMenuEntry.ITEM,
            title: 'nav:learn.finding-and-trialing-new-applicants',
            link: '/news/547-raiding-101-finding-and-trialing-your-ideal-applicant',
            wowIcon: 'icon_treasuremap'
          },
          {
            type: EMenuEntry.ITEM,
            title: 'nav:learn.choosing-officers',
            link: '/news/554-raiding-101-choosing-your-officers',
            wowIcon: 'inv_helm_crown_c_01_rose'
          },
          {
            type: EMenuEntry.ITEM,
            title: 'nav:learn.loot-distribution',
            link: '/news/555-raiding-101-is-loot-council-right-for-us',
            wowIcon: 'achievement_guildperk_mobilebanking'
          },
          {
            type: EMenuEntry.ITEM,
            title: 'nav:learn.performance-management',
            link: '/news/556-raiding-101-performance-management',
            wowIcon: 'spell_holy_powerinfusion'
          },
          {
            type: EMenuEntry.ITEM,
            title: 'nav:learn.conflict-management',
            wowIcon: 'warrior_disruptingshout',
            link: '/news/557-raiding-101-conflict-management-part-1',
            children: [
              {
                type: EMenuEntry.ITEM,
                title: () => t('nav:learn.series-with-part', { series: t('nav:learn.conflict-management'), number: 1 }),
                link: '/news/557-raiding-101-conflict-management-part-1',
                wowIcon: 'achievement_challengemode_bronze'
              },
              {
                type: EMenuEntry.ITEM,
                title: () => t('nav:learn.series-with-part', { series: t('nav:learn.conflict-management'), number: 2 }),
                link: '/news/565-raiding-101-conflict-management-part-2',
                wowIcon: 'achievement_challengemode_silver'
              }
            ]
          },
          {
            type: EMenuEntry.ITEM,
            title: 'nav:learn.team-morale',
            link: '/news/566-raiding-101-team-morale',
            wowIcon: 'ui_embercourt-emoji-veryhappy'
          },
          {
            type: EMenuEntry.ITEM,
            title: 'nav:learn.guild-communication',
            link: '/news/567-raiding-101-guild-communication',
            wowIcon: 'inv_relics_6orunestone_orcspeakingstaff'
          },
          {
            type: EMenuEntry.ITEM,
            title: 'nav:learn.guild-mergers',
            link: '/news/607-raiding-101-groups-and-guild-mergers',
            wowIcon: 'spell_nature_astralrecalgroup'
          },
          {
            type: EMenuEntry.ITEM,
            title: 'nav:learn.cliques',
            link: '/news/608-raiding-101-cliques',
            wowIcon: 'spell_misc_emotionsad'
          },
          {
            type: EMenuEntry.ITEM,
            title: 'nav:learn.referrals-and-social-members',
            link: '/news/605-raiding-101-social-casual-and-friend-of-friend-members',
            wowIcon: 'inv_misc_food_cooked_pabanquet_wok'
          },
          {
            type: EMenuEntry.ITEM,
            title: 'nav:learn.supporting-guildies-in-crisis',
            link: '/news/606-raiding-101-supporting-guildies-in-crisis',
            wowIcon: 'spell_chargepositive'
          }
        ]
      }
    ]
  },
  {
    type: EMenuEntry.ITEM,
    title: 'nav:news.feature-guides',
    link: '/news?tag=feature',
    wowIcon: 'trade_engineering',
    children: [
      {
        type: EMenuEntry.ITEM,
        title: 'nav:news.raiderio-features-and-services',
        link: '/news/660-raiderio-features-and-services',
        iconClassName: 'fa fa-fw fa-cubes'
      },
      {
        type: EMenuEntry.ITEM,
        title: 'nav:news.heatmaps',
        link: '/news/694-new-mythic-plus-heatmaps',
        iconClassName: 'fa fa-fw fa-chart-area'
      },
      {
        type: EMenuEntry.ITEM,
        title: 'nav:news.auto-route-creator',
        link: '/news/596-feature-the-auto-route-creator',
        iconClassName: 'fa fa-fw fa-route'
      },
      {
        type: EMenuEntry.ITEM,
        title: 'nav:news.mythic-plus-replays',
        link: '/news/597-feature-mythic-plus-replays',
        iconClassName: 'fa fa-fw fa-map-location-dot'
      },
      {
        type: EMenuEntry.ITEM,
        title: 'nav:news.helpful-addons-and-weakauras',
        link: '/news/659-must-have-addons-and-weakauras-for-the-war-within',
        iconClassName: 'fa fa-fw fa-magic-wand-sparkles'
      }
    ]
  },
  {
    type: EMenuEntry.DIVIDER
  },
  {
    type: EMenuEntry.ITEM,
    title: 'nav:main.read-latest-updates',
    link: '/news',
    wowIcon: 'inv_misc_paperbundle03c',
    children: NEWS_MENU_ITEMS
  }

  // {
  //   type: EMenuEntry.ITEM,
  //   title: 'nav:learn.tanking-tips',
  //   link: '/news/247-mythic-plus-101-tanking',
  //   wowIcon: 'ability_warrior_shieldmastery'
  // },
  // {
  //   type: EMenuEntry.ITEM,
  //   title: 'nav:learn.dps-tips',
  //   link: '/news/263-mythic-plus-101-dps',
  //   wowIcon: 'inv_sword_01'
  // },
  // {
  //   type: EMenuEntry.ITEM,
  //   title: 'nav:learn.healing-tips',
  //   link: '/news/286-mythic-plus-101-healing',
  //   wowIcon: 'petbattle_health'
  // },

  // {
  //   type: EMenuEntry.DIVIDER
  // },

  // {
  //   type: EMenuEntry.ITEM,
  //   title: 'common:expansions.shadowlands.name',
  //   wowIcon: 'inv_misc_token_boost_shadowlands',
  //   children: [
  //     {
  //       type: EMenuEntry.ITEM,
  //       title: 'nav:learn.know-your-tank',
  //       link: '/news/287-mythic-plus-101-know-your-tank',
  //       wowIcon: 'inv_shield_06'
  //     },
  //     {
  //       type: EMenuEntry.ITEM,
  //       title: 'nav:learn.know-your-healer',
  //       link: '/news/289-mythic-plus-101-know-your-healer',
  //       wowIcon: 'talentspec_druid_restoration'
  //     },
  //     {
  //       type: EMenuEntry.ITEM,
  //       title: 'nav:learn.know-your-melee-dps',
  //       link: '/news/288-mythic-plus-101-know-your-melee-dps',
  //       wowIcon: 'inv_sword_40'
  //     },
  //     {
  //       type: EMenuEntry.ITEM,
  //       title: 'nav:learn.know-your-ranged-dps',
  //       link: '/news/312-mythic-plus-101-know-your-ranged-dps',
  //       wowIcon: 'inv_crossbow_2h_broker_c_01'
  //     },
  //     {
  //       type: EMenuEntry.DIVIDER
  //     },
  //     {
  //       type: EMenuEntry.ITEM,
  //       title: () => t('nav:learn.expansion-affixes', { expansion: t('common:expansions.shadowlands.name') }),
  //       link: '/shadowlands-affixes',
  //       wowIcon: 'achievement_boss_archaedas'
  //     }
  //   ]
  // }
];

const MDI_SL_SEASON3_MENU_ITEMS: TMenuEntry[] = [
  // Mythic Dungeon International
  {
    type: EMenuEntry.ITEM,
    title: 'nav:mdi.mdi-highlights',
    link: resolveRoutePath(namedRoutes.TournamentGlobalCoverage, {
      tournament: 'shadowlands-season-3',
      noHash: true,
      noRealm: true
    })
  },
  {
    type: EMenuEntry.DIVIDER
  },
  {
    type: EMenuEntry.ITEM,
    title: 'nav:mdi.time-trials',
    link: resolveRoutePath(namedRoutes.MythicDungeonInternationalTimeTrialsOverviewSLSeason3, {
      trial: mdiSL3Config.currentTrial.slug
    }),
    children: [
      {
        type: EMenuEntry.ITEM,
        title: 'nav:mdi.global-teams',
        link: resolveRoutePath(namedRoutes.MythicDungeonInternationalTimeTrialsTeamRankingsSLSeason3, {
          trial: 'global'
        })
      },
      {
        type: EMenuEntry.ITEM,
        title: 'nav:mdi.global-dungeons',
        link: resolveRoutePath(namedRoutes.MythicDungeonInternationalTimeTrialsOverviewSLSeason3, {
          trial: 'global'
        })
      },
      {
        type: EMenuEntry.DIVIDER
      },
      {
        type: EMenuEntry.ITEM,
        title: 'nav:mdi.china-teams',
        link: resolveRoutePath(namedRoutes.MythicDungeonInternationalTimeTrialsTeamRankingsSLSeason3, {
          trial: 'china'
        })
      },
      {
        type: EMenuEntry.ITEM,
        title: 'nav:mdi.china-dungeons',
        link: resolveRoutePath(namedRoutes.MythicDungeonInternationalTimeTrialsOverviewSLSeason3, {
          trial: 'china'
        })
      }
    ]
  },
  {
    type: EMenuEntry.ITEM,
    title: 'nav:mdi.group-stage',
    link: resolveRoutePath(namedRoutes.MythicDungeonInternationalBracketSLSeason3, {
      noHash: true,
      noRealm: true,
      bracket: 'season-finals'
    }),
    children: [
      {
        type: EMenuEntry.ITEM,
        title: 'nav:mdi.global-group-a',
        link: resolveRoutePath(namedRoutes.MythicDungeonInternationalBracketSLSeason3, {
          noRealm: true,
          bracket: 'global-group-a'
        })
      },
      {
        type: EMenuEntry.ITEM,
        title: 'nav:mdi.global-group-b',
        link: resolveRoutePath(namedRoutes.MythicDungeonInternationalBracketSLSeason3, {
          noRealm: true,
          bracket: 'global-group-b'
        })
      },
      {
        type: EMenuEntry.ITEM,
        title: 'nav:mdi.global-group-c',
        link: resolveRoutePath(namedRoutes.MythicDungeonInternationalBracketSLSeason3, {
          noRealm: true,
          bracket: 'global-group-c'
        })
      },
      {
        type: EMenuEntry.DIVIDER
      },
      {
        type: EMenuEntry.ITEM,
        title: 'nav:mdi.china-finals',
        link: resolveRoutePath(namedRoutes.MythicDungeonInternationalBracketSLSeason3, {
          bracket: 'china-finals'
        })
      }
    ]
  },
  {
    type: EMenuEntry.ITEM,
    title: 'nav:mdi.last-stand',
    link: resolveRoutePath(namedRoutes.MythicDungeonInternationalTimeTrialsOverviewSLSeason3, {
      trial: mdiSL3Config.currentTrial.slug
    }),
    children: [
      {
        type: EMenuEntry.ITEM,
        title: 'nav:mdi.qualifiers-teams',
        link: resolveRoutePath(namedRoutes.MythicDungeonInternationalTimeTrialsTeamRankingsSLSeason3, {
          trial: 'last-stand-qualifiers'
        })
      },
      {
        type: EMenuEntry.ITEM,
        title: 'nav:mdi.qualifiers-dungeons',
        link: resolveRoutePath(namedRoutes.MythicDungeonInternationalTimeTrialsOverviewSLSeason3, {
          trial: 'last-stand-qualifiers'
        })
      },
      {
        type: EMenuEntry.DIVIDER
      },
      {
        type: EMenuEntry.ITEM,
        title: 'nav:mdi.tournament-teams',
        link: resolveRoutePath(namedRoutes.MythicDungeonInternationalTimeTrialsTeamRankingsSLSeason3, {
          trial: 'last-stand-tournament'
        })
      },
      {
        type: EMenuEntry.ITEM,
        title: 'nav:mdi.tournament-dungeons',
        link: resolveRoutePath(namedRoutes.MythicDungeonInternationalTimeTrialsOverviewSLSeason3, {
          trial: 'last-stand-tournament'
        })
      }
    ]
  },
  {
    type: EMenuEntry.ITEM,
    title: 'nav:mdi.season-finals',
    link: resolveRoutePath(namedRoutes.MythicDungeonInternationalBracketSLSeason3, {
      bracket: 'season-finals'
    })
  }
];

const MDI_SL_SEASON2_MENU_ITEMS: TMenuEntry[] = [
  // Mythic Dungeon International
  {
    type: EMenuEntry.ITEM,
    title: 'nav:mdi.highlights',
    link: resolveRoutePath(namedRoutes.TournamentGlobalCoverage, {
      tournament: 'shadowlands-season-2',
      noHash: true,
      noRealm: true
    })
  },
  {
    type: EMenuEntry.DIVIDER
  },
  {
    type: EMenuEntry.ITEM,
    title: 'nav:mdi.great-push-season-2',
    link: resolveRoutePath(namedRoutes.MythicDungeonInternationalGreatPushSeason2TournamentTeamRankings, {
      noHash: true,
      noRealm: true
    }),
    children: [
      {
        type: EMenuEntry.ITEM,
        title: 'nav:mdi.tournament-teams',
        link: resolveRoutePath(namedRoutes.MythicDungeonInternationalGreatPushSeason2TournamentTeamRankings, {
          noHash: true,
          noRealm: true
        })
      },
      {
        type: EMenuEntry.ITEM,
        title: 'nav:mdi.tournament-dungeons',
        link: resolveRoutePath(namedRoutes.MythicDungeonInternationalGreatPushSeason2TournamentMapRankings, {
          noHash: true,
          noRealm: true
        })
      },
      {
        type: EMenuEntry.ITEM,
        title: 'nav:mdi.proving-grounds-teams',
        link: resolveRoutePath(namedRoutes.MythicDungeonInternationalGreatPushSeason2QualifiersTeamRankings, {
          noHash: true,
          noRealm: true
        })
      },
      {
        type: EMenuEntry.ITEM,
        title: 'nav:mdi.proving-grounds-dungeons',
        link: resolveRoutePath(namedRoutes.MythicDungeonInternationalGreatPushSeason2QualifiersMapRankings, {
          noHash: true,
          noRealm: true
        })
      }
    ]
  },
  {
    type: EMenuEntry.DIVIDER
  },

  {
    type: EMenuEntry.ITEM,
    title: 'Season Finals (Phase 4)',
    link: resolveRoutePath(namedRoutes.MythicDungeonInternationalBracketSLSeason2, {
      bracket: 'season-finals'
    })
  },
  {
    type: EMenuEntry.ITEM,
    title: 'Last Stand (Phase 3)',
    link: resolveRoutePath(namedRoutes.MythicDungeonInternationalTimeTrialsOverviewSLSeason2, {
      trial: mdiSL2Config.currentTrial.slug
    }),
    children: [
      {
        type: EMenuEntry.ITEM,
        title: 'Qualifiers: Teams',
        link: resolveRoutePath(namedRoutes.MythicDungeonInternationalTimeTrialsTeamRankingsSLSeason2, {
          trial: 'last-stand-qualifiers'
        })
      },
      {
        type: EMenuEntry.ITEM,
        title: 'Qualifiers: Dungeons',
        link: resolveRoutePath(namedRoutes.MythicDungeonInternationalTimeTrialsOverviewSLSeason2, {
          trial: 'last-stand-qualifiers'
        })
      },
      {
        type: EMenuEntry.DIVIDER
      },
      {
        type: EMenuEntry.ITEM,
        title: 'nav:mdi.tournament-teams',
        link: resolveRoutePath(namedRoutes.MythicDungeonInternationalTimeTrialsTeamRankingsSLSeason2, {
          trial: 'last-stand-tournament'
        })
      },
      {
        type: EMenuEntry.ITEM,
        title: 'nav:mdi.tournament-dungeons',
        link: resolveRoutePath(namedRoutes.MythicDungeonInternationalTimeTrialsOverviewSLSeason2, {
          trial: 'last-stand-tournament'
        })
      }
    ]
  },

  {
    type: EMenuEntry.ITEM,
    title: 'Group Stage (Phase 2)',
    link: resolveRoutePath(namedRoutes.MythicDungeonInternationalBracketSLSeason2, {
      noHash: true,
      noRealm: true,
      bracket: mdiSL2Config.currentBracket.slug
    }),
    children: [
      {
        type: EMenuEntry.ITEM,
        title: 'Global: Group A',
        link: resolveRoutePath(namedRoutes.MythicDungeonInternationalBracketSLSeason2, {
          noRealm: true,
          bracket: 'global-group-a'
        })
      },
      {
        type: EMenuEntry.ITEM,
        title: 'Global: Group B',
        link: resolveRoutePath(namedRoutes.MythicDungeonInternationalBracketSLSeason2, {
          noRealm: true,
          bracket: 'global-group-b'
        })
      },
      {
        type: EMenuEntry.ITEM,
        title: 'Global: Group C',
        link: resolveRoutePath(namedRoutes.MythicDungeonInternationalBracketSLSeason2, {
          noRealm: true,
          bracket: 'global-group-c'
        })
      },
      {
        type: EMenuEntry.DIVIDER
      },
      {
        type: EMenuEntry.ITEM,
        title: 'China: Finals',
        link: resolveRoutePath(namedRoutes.MythicDungeonInternationalBracketSLSeason2, {
          bracket: 'china-finals'
        })
      }
    ]
  },

  {
    type: EMenuEntry.ITEM,
    title: 'Time Trials (Phase 1)',
    link: resolveRoutePath(namedRoutes.MythicDungeonInternationalTimeTrialsOverviewSLSeason2, {
      trial: mdiSL2Config.currentTrial.slug
    }),
    children: [
      {
        type: EMenuEntry.ITEM,
        title: 'Global: Teams',
        link: resolveRoutePath(namedRoutes.MythicDungeonInternationalTimeTrialsTeamRankingsSLSeason2, {
          trial: 'global'
        })
      },
      {
        type: EMenuEntry.ITEM,
        title: 'Global: Dungeons',
        link: resolveRoutePath(namedRoutes.MythicDungeonInternationalTimeTrialsOverviewSLSeason2, {
          trial: 'global'
        })
      },
      {
        type: EMenuEntry.DIVIDER
      },
      {
        type: EMenuEntry.ITEM,
        title: 'China: Teams',
        link: resolveRoutePath(namedRoutes.MythicDungeonInternationalTimeTrialsTeamRankingsSLSeason2, {
          trial: 'china'
        })
      },
      {
        type: EMenuEntry.ITEM,
        title: 'China: Dungeons',
        link: resolveRoutePath(namedRoutes.MythicDungeonInternationalTimeTrialsOverviewSLSeason2, {
          trial: 'china'
        })
      }
    ]
  }
];

const MDI_DF_SEASON1_MENU_ITEMS: TMenuEntry[] = [
  // Mythic Dungeon International
  {
    type: EMenuEntry.ITEM,
    title: 'nav:mdi.mdi-highlights',
    link: resolveRoutePath(namedRoutes.TournamentGlobalCoverage, {
      tournament: 'dragonflight-season-1',
      noHash: true,
      noRealm: true
    })
  },
  {
    type: EMenuEntry.DIVIDER
  },
  {
    type: EMenuEntry.ITEM,
    title: 'nav:mdi.time-trials',
    link: resolveRoutePath(namedRoutes.MythicDungeonInternationalTimeTrialsOverviewDFSeason1, {
      trial: mdiDF1Config.currentTrial.slug
    }),
    children: [
      {
        type: EMenuEntry.ITEM,
        title: 'nav:mdi.global-teams',
        link: resolveRoutePath(namedRoutes.MythicDungeonInternationalTimeTrialsTeamRankingsDFSeason1, {
          trial: 'global'
        })
      },
      {
        type: EMenuEntry.ITEM,
        title: 'nav:mdi.global-dungeons',
        link: resolveRoutePath(namedRoutes.MythicDungeonInternationalTimeTrialsOverviewDFSeason1, {
          trial: 'global'
        })
      }
    ]
  },
  {
    type: EMenuEntry.ITEM,
    title: 'nav:mdi.group-stage',
    link: resolveRoutePath(namedRoutes.MythicDungeonInternationalBracketDFSeason1, {
      noHash: true,
      noRealm: true,
      bracket: 'finals'
    }),
    children: [
      {
        type: EMenuEntry.ITEM,
        title: 'nav:mdi.global-group-a',
        link: resolveRoutePath(namedRoutes.MythicDungeonInternationalBracketDFSeason1, {
          noRealm: true,
          bracket: 'group-a'
        })
      },
      {
        type: EMenuEntry.ITEM,
        title: 'nav:mdi.global-group-b',
        link: resolveRoutePath(namedRoutes.MythicDungeonInternationalBracketDFSeason1, {
          noRealm: true,
          bracket: 'group-b'
        })
      },
      {
        type: EMenuEntry.ITEM,
        title: 'nav:mdi.global-group-c',
        link: resolveRoutePath(namedRoutes.MythicDungeonInternationalBracketDFSeason1, {
          noRealm: true,
          bracket: 'group-c'
        })
      }
    ]
  },
  {
    type: EMenuEntry.ITEM,
    title: 'nav:mdi.last-stand',
    link: resolveRoutePath(namedRoutes.MythicDungeonInternationalTimeTrialsOverviewDFSeason1, {
      trial: mdiDF1Config.currentTrial.slug
    }),
    children: [
      {
        type: EMenuEntry.ITEM,
        title: 'nav:mdi.qualifiers-teams',
        link: resolveRoutePath(namedRoutes.MythicDungeonInternationalTimeTrialsTeamRankingsDFSeason1, {
          trial: 'last-stand-qualifiers'
        })
      },
      {
        type: EMenuEntry.ITEM,
        title: 'nav:mdi.qualifiers-dungeons',
        link: resolveRoutePath(namedRoutes.MythicDungeonInternationalTimeTrialsOverviewDFSeason1, {
          trial: 'last-stand-qualifiers'
        })
      },
      {
        type: EMenuEntry.DIVIDER
      },
      {
        type: EMenuEntry.ITEM,
        title: 'nav:mdi.tournament-teams',
        link: resolveRoutePath(namedRoutes.MythicDungeonInternationalTimeTrialsTeamRankingsDFSeason1, {
          trial: 'last-stand-tournament'
        })
      },
      {
        type: EMenuEntry.ITEM,
        title: 'nav:mdi.tournament-dungeons',
        link: resolveRoutePath(namedRoutes.MythicDungeonInternationalTimeTrialsOverviewDFSeason1, {
          trial: 'last-stand-tournament'
        })
      }
    ]
  },
  {
    type: EMenuEntry.ITEM,
    title: 'nav:mdi.season-finals',
    link: resolveRoutePath(namedRoutes.MythicDungeonInternationalBracketDFSeason1, {
      bracket: 'finals'
    })
  }
];

const GREAT_PUSH_DF_SEASON_2_MENU_ITEMS: TMenuEntry[] = [
  // Mythic Dungeon International Dragonflight Season 2 Great Push
  {
    type: EMenuEntry.ITEM,
    title: 'nav:mdi.great-push-highlights',
    link: resolveRoutePath(namedRoutes.TournamentGlobalCoverage, {
      tournament: 'dragonflight-season-2',
      noHash: true,
      noRealm: true
    })
  },
  {
    type: EMenuEntry.DIVIDER
  },
  {
    type: EMenuEntry.ITEM,
    title: 'Proving Grounds',
    link: resolveRoutePath(namedRoutes.MythicDungeonInternationalGreatPushDFSeason2QualifiersMapRankings, {
      noHash: true,
      noRealm: true
    }),
    children: [
      {
        type: EMenuEntry.ITEM,
        title: 'nav:mdi.dungeon-leaderboards',
        link: resolveRoutePath(namedRoutes.MythicDungeonInternationalGreatPushDFSeason2QualifiersMapRankings, {
          noHash: true,
          noRealm: true
        })
      },
      {
        type: EMenuEntry.ITEM,
        title: 'nav:mdi.team-leaderboards',
        link: resolveRoutePath(namedRoutes.MythicDungeonInternationalGreatPushDFSeason2QualifiersTeamRankings, {
          noHash: true,
          noRealm: true
        })
      }
    ]
  },
  {
    type: EMenuEntry.ITEM,
    title: 'nav:mdi.group-stage',
    link: resolveRoutePath(namedRoutes.MythicDungeonInternationalGreatPushDFSeason2TournamentMapRankings, {
      noHash: true,
      noRealm: true,
      bracket: 'global-finals'
    }),
    children: [
      {
        type: EMenuEntry.ITEM,
        title: 'nav:mdi.global-group-a',
        link: resolveRoutePath(namedRoutes.MythicDungeonInternationalGreatPushDFSeason2TournamentMapRankings, {
          noRealm: true,
          bracket: 'group-a'
        })
      },
      {
        type: EMenuEntry.ITEM,
        title: 'nav:mdi.global-group-b',
        link: resolveRoutePath(namedRoutes.MythicDungeonInternationalGreatPushDFSeason2TournamentMapRankings, {
          noRealm: true,
          bracket: 'group-b'
        })
      },
      {
        type: EMenuEntry.ITEM,
        title: 'nav:mdi.global-group-c',
        link: resolveRoutePath(namedRoutes.MythicDungeonInternationalGreatPushDFSeason2TournamentMapRankings, {
          noRealm: true,
          bracket: 'group-c'
        })
      }
    ]
  },
  {
    type: EMenuEntry.ITEM,
    title: 'nav:mdi.season-finals',
    link: resolveRoutePath(namedRoutes.MythicDungeonInternationalGreatPushDFSeason2TournamentTeamRankings, {
      bracket: 'global-finals'
    })
  }
];

const BLIZZCON_GUILD_CLASH_2023_MENU_ITEMS: TMenuEntry[] = [
  // Blizzcon Guild Clash (M+ & Arena)
  {
    type: EMenuEntry.ITEM,
    title: 'BlizzCon Guild Clash (2023)',
    link: resolveRoutePath(namedRoutes.BlizzconGuildClash2023, {
      bracket: 'mythic-plus',
      noHash: true,
      noRealm: true
    }),
    children: [
      {
        type: EMenuEntry.ITEM,
        title: 'Guild Clash Mythic Plus',
        link: resolveRoutePath(namedRoutes.BlizzconGuildClash2023, {
          bracket: 'mythic-plus',
          noHash: true,
          noRealm: true
        })
      },
      {
        type: EMenuEntry.ITEM,
        title: 'Guild Clash Arena',
        link: resolveRoutePath(namedRoutes.BlizzconGuildClash2023, {
          bracket: 'arena',
          noHash: true,
          noRealm: true
        })
      }
    ]
  }
];

const MDI_SL_SEASON4_MENU_ITEMS: TMenuEntry[] = [
  {
    type: EMenuEntry.ITEM,
    title: 'nav:mdi.great-push-highlights',
    link: resolveRoutePath(namedRoutes.TournamentGlobalCoverage, {
      tournament: 'shadowlands-season-4',
      noHash: true,
      noRealm: true
    })
  },
  {
    type: EMenuEntry.DIVIDER
  },

  {
    type: EMenuEntry.ITEM,
    title: 'Global Finals',
    link: resolveRoutePath(namedRoutes.MythicDungeonInternationalGreatPushSeason4TournamentMapRankings, {
      bracket: 'grand-finals',
      noHash: true,
      noRealm: true
    }),
    children: [
      {
        type: EMenuEntry.ITEM,
        title: 'nav:mdi.dungeon-leaderboards',
        link: resolveRoutePath(namedRoutes.MythicDungeonInternationalGreatPushSeason4TournamentMapRankings, {
          bracket: 'grand-finals',
          noHash: true,
          noRealm: true
        })
      },
      {
        type: EMenuEntry.ITEM,
        title: 'nav:mdi.team-leaderboards',
        link: resolveRoutePath(namedRoutes.MythicDungeonInternationalGreatPushSeason4TournamentTeamRankings, {
          bracket: 'grand-finals',
          noHash: true,
          noRealm: true
        })
      }
    ]
  },

  {
    type: EMenuEntry.ITEM,
    title: 'AMER Regionals',
    link: resolveRoutePath(namedRoutes.MythicDungeonInternationalGreatPushSeason4TournamentMapRankings, {
      bracket: 'amer',
      noHash: true,
      noRealm: true
    }),
    children: [
      {
        type: EMenuEntry.ITEM,
        title: 'nav:mdi.dungeon-leaderboards',
        link: resolveRoutePath(namedRoutes.MythicDungeonInternationalGreatPushSeason4TournamentMapRankings, {
          bracket: 'amer',
          noHash: true,
          noRealm: true
        })
      },
      {
        type: EMenuEntry.ITEM,
        title: 'nav:mdi.team-leaderboards',
        link: resolveRoutePath(namedRoutes.MythicDungeonInternationalGreatPushSeason4TournamentTeamRankings, {
          bracket: 'amer',
          noHash: true,
          noRealm: true
        })
      }
    ]
  },

  {
    type: EMenuEntry.ITEM,
    title: 'EMEA Regionals',
    link: resolveRoutePath(namedRoutes.MythicDungeonInternationalGreatPushSeason4TournamentMapRankings, {
      bracket: 'emea',
      noHash: true,
      noRealm: true
    }),
    children: [
      {
        type: EMenuEntry.ITEM,
        title: 'nav:mdi.dungeon-leaderboards',
        link: resolveRoutePath(namedRoutes.MythicDungeonInternationalGreatPushSeason4TournamentMapRankings, {
          bracket: 'emea',
          noHash: true,
          noRealm: true
        })
      },
      {
        type: EMenuEntry.ITEM,
        title: 'nav:mdi.team-leaderboards',
        link: resolveRoutePath(namedRoutes.MythicDungeonInternationalGreatPushSeason4TournamentTeamRankings, {
          bracket: 'emea',
          noHash: true,
          noRealm: true
        })
      }
    ]
  },

  {
    type: EMenuEntry.ITEM,
    title: 'China Regionals',
    link: resolveRoutePath(namedRoutes.MythicDungeonInternationalGreatPushSeason4TournamentMapRankings, {
      bracket: 'china',
      noHash: true,
      noRealm: true
    }),
    children: [
      {
        type: EMenuEntry.ITEM,
        title: 'nav:mdi.dungeon-leaderboards',
        link: resolveRoutePath(namedRoutes.MythicDungeonInternationalGreatPushSeason4TournamentMapRankings, {
          bracket: 'china',
          noHash: true,
          noRealm: true
        })
      },
      {
        type: EMenuEntry.ITEM,
        title: 'nav:mdi.team-leaderboards',
        link: resolveRoutePath(namedRoutes.MythicDungeonInternationalGreatPushSeason4TournamentTeamRankings, {
          bracket: 'china',
          noHash: true,
          noRealm: true
        })
      }
    ]
  },

  {
    type: EMenuEntry.DIVIDER
  },

  {
    type: EMenuEntry.ITEM,
    title: 'AMER Proving Grounds',
    link: resolveRoutePath(namedRoutes.MythicDungeonInternationalGreatPushSeason4QualifiersMapRankings, {
      bracket: 'amer',
      noHash: true,
      noRealm: true
    }),
    children: [
      {
        type: EMenuEntry.ITEM,
        title: 'nav:mdi.dungeon-leaderboards',
        link: resolveRoutePath(namedRoutes.MythicDungeonInternationalGreatPushSeason4QualifiersMapRankings, {
          bracket: 'amer',
          noHash: true,
          noRealm: true
        })
      },
      {
        type: EMenuEntry.ITEM,
        title: 'nav:mdi.team-leaderboards',
        link: resolveRoutePath(namedRoutes.MythicDungeonInternationalGreatPushSeason4QualifiersTeamRankings, {
          bracket: 'amer',
          noHash: true,
          noRealm: true
        })
      }
    ]
  },

  {
    type: EMenuEntry.ITEM,
    title: 'EMEA Proving Grounds',
    link: resolveRoutePath(namedRoutes.MythicDungeonInternationalGreatPushSeason4QualifiersMapRankings, {
      bracket: 'emea',
      noHash: true,
      noRealm: true
    }),
    children: [
      {
        type: EMenuEntry.ITEM,
        title: 'nav:mdi.dungeon-leaderboards',
        link: resolveRoutePath(namedRoutes.MythicDungeonInternationalGreatPushSeason4QualifiersMapRankings, {
          bracket: 'emea',
          noHash: true,
          noRealm: true
        })
      },
      {
        type: EMenuEntry.ITEM,
        title: 'nav:mdi.team-leaderboards',
        link: resolveRoutePath(namedRoutes.MythicDungeonInternationalGreatPushSeason4QualifiersTeamRankings, {
          bracket: 'emea',
          noHash: true,
          noRealm: true
        })
      }
    ]
  },

  {
    type: EMenuEntry.ITEM,
    title: 'China Proving Grounds',
    link: resolveRoutePath(namedRoutes.MythicDungeonInternationalGreatPushSeason4QualifiersMapRankings, {
      bracket: 'china',
      noHash: true,
      noRealm: true
    }),
    children: [
      {
        type: EMenuEntry.ITEM,
        title: 'nav:mdi.dungeon-leaderboards',
        link: resolveRoutePath(namedRoutes.MythicDungeonInternationalGreatPushSeason4QualifiersMapRankings, {
          bracket: 'china',
          noHash: true,
          noRealm: true
        })
      },
      {
        type: EMenuEntry.ITEM,
        title: 'nav:mdi.team-leaderboards',
        link: resolveRoutePath(namedRoutes.MythicDungeonInternationalGreatPushSeason4QualifiersTeamRankings, {
          bracket: 'china',
          noHash: true,
          noRealm: true
        })
      }
    ]
  }
];

const MDI_EVENT_MENU_CHILDREN: TMenuEntry[] = [
  ...BLIZZCON_GUILD_CLASH_2023_MENU_ITEMS,
  {
    type: EMenuEntry.ITEM,
    title: () =>
      t('nav:mdi.mdi-with-expnasion-season', {
        expansion: t('common:expansions.dragonflight.name'),
        seasonNumber: 2
      }),
    children: GREAT_PUSH_DF_SEASON_2_MENU_ITEMS
  },
  {
    type: EMenuEntry.ITEM,
    title: () =>
      t('nav:mdi.mdi-with-expnasion-season', {
        expansion: t('common:expansions.dragonflight.name'),
        seasonNumber: 1
      }),
    children: MDI_DF_SEASON1_MENU_ITEMS
  },
  {
    type: EMenuEntry.ITEM,
    title: 'MDI: Shadowlands Great Push',
    link: resolveRoutePath(namedRoutes.TournamentGlobalCoverage, {
      tournament: 'shadowlands-season-4',
      noHash: true,
      noRealm: true
    }),
    children: MDI_SL_SEASON4_MENU_ITEMS
  },
  // Mythic Dungeon International

  // MDI SL Season 3
  {
    type: EMenuEntry.ITEM,
    title: 'MDI: Shadowlands Season 3',
    link: '/mythic-dungeon-international',
    children: MDI_SL_SEASON3_MENU_ITEMS
  },

  // MDI SL Season 2
  {
    type: EMenuEntry.ITEM,
    title: 'MDI: Shadowlands Season 2',
    link: '/mythic-dungeon-international',
    children: MDI_SL_SEASON2_MENU_ITEMS
  },

  // MDI SL Season 1
  {
    type: EMenuEntry.ITEM,
    title: 'MDI: Shadowlands Season 1',
    link: resolveRoutePath(namedRoutes.MythicDungeonInternationalBracketSLSeason1, {
      noHash: true,
      noRealm: true,
      bracket: mdiSL1Config.currentBracket.slug
    }),
    children: [
      {
        type: EMenuEntry.ITEM,
        title: 'nav:mdi.great-push',
        link: resolveRoutePath(namedRoutes.MythicDungeonInternationalGreatPushTournamentTeamRankings, {
          noHash: true,
          noRealm: true
        }),
        children: [
          {
            type: EMenuEntry.ITEM,
            title: 'nav:mdi.highlights',
            className: 'text-bold',
            link: resolveRoutePath(namedRoutes.TournamentGlobalCoverage, {
              tournament: 'the-great-push',
              noHash: true,
              noRealm: true
            })
          },
          {
            type: EMenuEntry.DIVIDER
          },
          {
            type: EMenuEntry.ITEM,
            title: 'nav:mdi.tournament-teams',
            link: resolveRoutePath(namedRoutes.MythicDungeonInternationalGreatPushTournamentTeamRankings, {
              noHash: true,
              noRealm: true
            })
          },
          {
            type: EMenuEntry.ITEM,
            title: 'nav:mdi.tournament-dungeons',
            link: resolveRoutePath(namedRoutes.MythicDungeonInternationalGreatPushTournamentMapRankings, {
              noHash: true,
              noRealm: true
            })
          },
          {
            type: EMenuEntry.ITEM,
            title: 'nav:mdi.proving-grounds-teams',
            link: resolveRoutePath(namedRoutes.MythicDungeonInternationalGreatPushQualifiersTeamRankings, {
              noHash: true,
              noRealm: true
            })
          },
          {
            type: EMenuEntry.ITEM,
            title: 'nav:mdi.proving-grounds-dungeons',
            link: resolveRoutePath(namedRoutes.MythicDungeonInternationalGreatPushQualifiersMapRankings, {
              noHash: true,
              noRealm: true
            })
          }
        ]
      },
      {
        type: EMenuEntry.ITEM,
        title: 'Cup Brackets',
        link: resolveRoutePath(namedRoutes.MythicDungeonInternationalBracketSLSeason1, {
          noHash: true,
          noRealm: true,
          bracket: mdiSL1Config.currentBracket.slug
        }),
        children: [
          {
            type: EMenuEntry.ITEM,
            title: 'Global Finals',
            link: resolveRoutePath(namedRoutes.MythicDungeonInternationalBracketSLSeason1, {
              noRealm: true,
              bracket: 'global-finals'
            })
          },
          ..._.range(mdiSL1Config.currentCupWeeks.global, 0).map((num: number) => ({
            type: EMenuEntry.ITEM,
            title: `Global Cup ${num}`,
            link: resolveRoutePath(namedRoutes.MythicDungeonInternationalBracketSLSeason1, {
              bracket: `global-cup-${num}`
            })
          })),
          {
            type: EMenuEntry.DIVIDER
          },
          {
            type: EMenuEntry.ITEM,
            title: 'nav:mdi.china-finals',
            link: resolveRoutePath(namedRoutes.MythicDungeonInternationalBracketSLSeason1, {
              bracket: 'china-finals'
            })
          },

          ..._.range(mdiSL1Config.currentCupWeeks.china, 0).map((num: number) => ({
            type: EMenuEntry.ITEM,
            title: `China Cup ${num}`,
            link: resolveRoutePath(namedRoutes.MythicDungeonInternationalBracketSLSeason1, {
              bracket: `china-cup-${num}`
            })
          }))
        ]
      },
      {
        type: EMenuEntry.ITEM,
        title: 'nav:mdi.time-trials-runs',
        link: resolveRoutePath(namedRoutes.MythicDungeonInternationalTimeTrialsOverviewSLSeason1, {
          trial: mdiSL1Config.currentTrial.slug
        }),
        children: [
          ..._.range(1, mdiSL1Config.currentTimeTrialWeeks.global + 1).map((num: number) => ({
            type: EMenuEntry.ITEM,
            link: resolveRoutePath(namedRoutes.MythicDungeonInternationalTimeTrialsOverviewSLSeason1, {
              trial: `global-${num}`
            }),
            title: `Global: Week ${num}`
          })),

          ..._.range(1, mdiSL1Config.currentTimeTrialWeeks.china + 1).map((num: number) => ({
            type: EMenuEntry.ITEM,
            link: resolveRoutePath(namedRoutes.MythicDungeonInternationalTimeTrialsOverviewSLSeason1, {
              trial: `china-${num}`
            }),
            title: `China: Week ${num}`
          }))
        ]
      },
      {
        type: EMenuEntry.ITEM,
        title: 'nav:mdi.time-trials-teams',
        link: resolveRoutePath(namedRoutes.MythicDungeonInternationalTimeTrialsTeamRankingsSLSeason1, {
          division: mdiSL1Config.currentTrial.slug,
          region: 'world'
        }),
        children: [
          ...[
            ...(mdiSL1Config.currentTimeTrialWeeks.global >= 1 ? [{ slug: 'global-1', name: 'Global: Week 1' }] : []),
            ...(mdiSL1Config.currentTimeTrialWeeks.global >= 2 ? [{ slug: 'global-2', name: 'Global: Week 2' }] : []),
            ...(mdiSL1Config.currentTimeTrialWeeks.global >= 3 ? [{ slug: 'global-3', name: 'Global: Week 3' }] : []),
            ...(mdiSL1Config.currentTimeTrialWeeks.global >= 4 ? [{ slug: 'global-4', name: 'Global: Week 4' }] : []),
            ...(mdiSL1Config.currentTimeTrialWeeks.china >= 1 ? [{ slug: 'china-1', name: 'China: Week 1' }] : []),
            ...(mdiSL1Config.currentTimeTrialWeeks.china >= 2 ? [{ slug: 'china-2', name: 'China: Week 2' }] : []),
            ...(mdiSL1Config.currentTimeTrialWeeks.china >= 3 ? [{ slug: 'china-3', name: 'China: Week 3' }] : [])
          ].map((reg: any) => ({
            type: EMenuEntry.ITEM,
            title: reg.name,
            link: `/mythic-dungeon-international/sl-season-1-time-trials-teams/${reg.slug}/world`
          }))
        ]
      }
    ]
  },

  // MDI BFA Season 4
  {
    type: EMenuEntry.ITEM,
    title: 'MDI BFA Season 4',
    link: resolveRoutePath(namedRoutes.MythicDungeonInternationalBracketBfa2020, {
      noHash: true,
      noRealm: true,
      bracket: mdiBfa2020Config.currentBracket.slug
    }),
    children: [
      {
        type: EMenuEntry.ITEM,
        title: 'Cup Brackets',
        link: resolveRoutePath(namedRoutes.MythicDungeonInternationalBracketBfa2020, {
          noHash: true,
          noRealm: true,
          bracket: mdiBfa2020Config.currentBracket.slug
        }),
        children: [
          ...[1, 2, 3].map((num: number) => ({
            type: EMenuEntry.ITEM,
            title: `EU & Asia Cup ${num}`,
            link: resolveRoutePath(namedRoutes.MythicDungeonInternationalBracketBfa2020, {
              bracket: `euasia-cup-${num}`
            })
          })),
          ...[1, 2, 3].map((num: number) => ({
            type: EMenuEntry.ITEM,
            title: `AMER Cup ${num}`,
            link: resolveRoutePath(namedRoutes.MythicDungeonInternationalBracketBfa2020, {
              bracket: `amer-cup-${num}`
            })
          })),
          {
            type: EMenuEntry.ITEM,
            title: 'Global Finals',
            link: resolveRoutePath(namedRoutes.MythicDungeonInternationalBracketBfa2020, {
              noRealm: true,
              bracket: 'global-finals'
            })
          }
        ]
      },
      {
        type: EMenuEntry.ITEM,
        title: 'nav:mdi.time-trials-runs',
        link: resolveRoutePath(namedRoutes.MythicDungeonInternationalTimeTrialsOverviewBfa2020, {
          trial: mdiBfa2020Config.currentTrial.slug
        }),
        children: [
          ...[1, 2, 3].map<TMenuEntry>((num: number) => ({
            type: EMenuEntry.ITEM,
            link: resolveRoutePath(namedRoutes.MythicDungeonInternationalTimeTrialsOverviewBfa2020, {
              trial: `euasia-${num}`
            }),
            title: `EU & Asia: Week ${num}`
          })),
          ...[1, 2, 3].map<TMenuEntry>((num: number) => ({
            type: EMenuEntry.ITEM,
            link: resolveRoutePath(namedRoutes.MythicDungeonInternationalTimeTrialsOverviewBfa2020, {
              trial: `amer-${num}`
            }),
            title: `AMER: Week ${num}`
          }))
        ]
      },
      {
        type: EMenuEntry.ITEM,
        title: 'nav:mdi.time-trials-teams',
        link: resolveRoutePath(namedRoutes.MythicDungeonInternationalTimeTrialsTeamRankingsBfa2020, {
          division: mdiBfa2020Config.currentTrial.slug,
          region: 'world'
        }),
        children: [
          ...[
            { slug: 'euasia-1', name: 'EU & Asia: Week 1' },
            { slug: 'euasia-2', name: 'EU & Asia: Week 2' },
            { slug: 'euasia-3', name: 'EU & Asia: Week 3' },
            { slug: 'amer-1', name: 'AMER: Week 1' },
            { slug: 'amer-2', name: 'AMER: Week 2' },
            { slug: 'amer-3', name: 'AMER: Week 3' }
          ].map((reg: any) => ({
            type: EMenuEntry.ITEM,
            title: reg.name,
            link: `/mythic-dungeon-international/bfa-season-4-time-trials-teams/${reg.slug}/world`
          }))
        ]
      }
    ]
  },

  // MDI Summer 2019
  {
    type: EMenuEntry.ITEM,
    title: 'MDI Summer Season (2019)',
    link: '/mythic-dungeon-international/summer-2019-brackets/blizzcon-finals',
    children: [
      {
        type: EMenuEntry.ITEM,
        title: 'Team Standings',
        link: '/mythic-dungeon-international/summer-2019-standings'
      },
      {
        type: EMenuEntry.ITEM,
        title: 'Blizzcon Finals',
        link: '/mythic-dungeon-international/summer-2019-brackets/blizzcon-finals'
      },
      {
        type: EMenuEntry.ITEM,
        title: 'Cup Brackets',
        link: resolveRoutePath(namedRoutes.MythicDungeonInvitationalSummer2019Overview, {
          bracket: 'west-cup-1'
        }),
        children: [
          ...[1, 2, 3].map((num: number) => ({
            type: EMenuEntry.ITEM,
            title: `West Cup ${num}`,
            link: resolveRoutePath(namedRoutes.MythicDungeonInvitationalSummer2019Overview, {
              bracket: `west-cup-${num}`
            })
          })),
          ...[1, 2, 3].map((num: number) => ({
            type: EMenuEntry.ITEM,
            title: `East Cup ${num}`,
            link: resolveRoutePath(namedRoutes.MythicDungeonInvitationalSummer2019Overview, {
              bracket: `east-cup-${num}`
            })
          }))
        ]
      },

      {
        type: EMenuEntry.ITEM,
        title: 'nav:mdi.time-trials-runs',
        link: resolveRoutePath(namedRoutes.MythicDungeonInvitationalSummer2019TimeTrialsOverview, {
          trial: 'west-1'
        }),
        children: [
          ...[1, 2, 3].map((num: number) => ({
            type: EMenuEntry.ITEM,
            title: `West: Week ${num}`,
            link: resolveRoutePath(namedRoutes.MythicDungeonInvitationalSummer2019TimeTrialsOverview, {
              trial: `west-${num}`
            })
          })),

          ...[1, 2, 3].map((num: number) => ({
            type: EMenuEntry.ITEM,
            title: `East: Week ${num}`,
            link: resolveRoutePath(namedRoutes.MythicDungeonInvitationalSummer2019TimeTrialsOverview, {
              trial: `east-${num}`
            })
          }))
        ]
      },

      {
        type: EMenuEntry.ITEM,
        title: 'nav:mdi.time-trials-teams',
        link: resolveRoutePath(namedRoutes.MythicDungeonInvitationalSummer2019TimeTrialsOverview, {
          trial: 'west-1'
        }),
        children: [
          ...[
            { slug: 'west-1', name: 'West: Week 1' },
            { slug: 'west-2', name: 'West: Week 2' },
            { slug: 'west-3', name: 'West: Week 3' },
            { slug: 'east-1', name: 'East: Week 1' },
            { slug: 'east-2', name: 'East: Week 2' },
            { slug: 'east-3', name: 'East: Week 3' }
          ].map((reg: any) => ({
            type: EMenuEntry.ITEM,
            title: reg.name,
            link: `/mythic-dungeon-international/summer-2019-time-trials-teams/${reg.slug}/world`
          }))
        ]
      }
    ]
  },

  // MDI Spring 2019
  {
    type: EMenuEntry.ITEM,
    title: 'MDI Spring Season (2019)',
    link: '/mythic-dungeon-international/spring-2019-finals',
    children: [
      {
        type: EMenuEntry.ITEM,
        title: 'Team Standings',
        link: '/mythic-dungeon-international/spring-2019-standings'
      },
      {
        type: EMenuEntry.ITEM,
        title: 'Global Finals',
        link: '/mythic-dungeon-international/spring-2019-finals'
      },
      {
        type: EMenuEntry.ITEM,
        title: 'Cup Brackets',
        link: resolveRoutePath(namedRoutes.MythicDungeonInvitationalSpring2019TimeTrialsOverview, {
          trial: 'west-1'
        }),
        children: [
          ...[1, 2, 3].map((num: number) => ({
            type: EMenuEntry.ITEM,
            title: `West Cup ${num}`,
            link: resolveRoutePath(namedRoutes.MythicDungeonInvitationalSpring2019Overview, {
              bracket: `west-cup-${num}`
            })
          })),

          ...[1, 2, 3].map((num: number) => ({
            type: EMenuEntry.ITEM,
            title: `East Cup ${num}`,
            link: resolveRoutePath(namedRoutes.MythicDungeonInvitationalSpring2019Overview, {
              bracket: `east-cup-${num}`
            })
          }))
        ]
      },
      {
        type: EMenuEntry.ITEM,
        title: 'nav:mdi.time-trials-runs',
        link: resolveRoutePath(namedRoutes.MythicDungeonInvitationalSpring2019TimeTrialsOverview, {
          trial: 'west-1'
        }),
        children: [
          ...[1, 2, 3].map((num: number) => ({
            type: EMenuEntry.ITEM,
            title: `West: Week ${num}`,
            link: resolveRoutePath(namedRoutes.MythicDungeonInvitationalSpring2019TimeTrialsOverview, {
              trial: `west-${num}`
            })
          })),

          ...[1, 2, 3].map((num: number) => ({
            type: EMenuEntry.ITEM,
            title: `East: Week ${num}`,
            link: resolveRoutePath(namedRoutes.MythicDungeonInvitationalSpring2019TimeTrialsOverview, {
              trial: `east-${num}`
            })
          }))
        ]
      },
      {
        type: EMenuEntry.ITEM,
        title: 'nav:mdi.time-trials-teams',
        link: resolveRoutePath(namedRoutes.MythicDungeonInvitationalSpring2019TimeTrialsOverview, {
          trial: 'west-1'
        }),
        children: [
          ...[
            { slug: 'west-1', name: 'West: Week 1' },
            { slug: 'west-2', name: 'West: Week 2' },
            { slug: 'west-3', name: 'West: Week 3' },
            { slug: 'east-1', name: 'East: Week 1' },
            { slug: 'east-2', name: 'East: Week 2' },
            { slug: 'east-3', name: 'East: Week 3' }
          ].map((reg: any) => ({
            type: EMenuEntry.ITEM,
            title: reg.name,
            link: `/mythic-dungeon-international/spring-2019-time-trials-teams/${reg.slug}/world`
          }))
        ]
      },
      {
        type: EMenuEntry.ITEM,
        title: 'Proving Grounds',
        link: resolveRoutePath(namedRoutes.MythicDungeonInvitationalSpring2019ProvingGrounds),
        children: [
          {
            type: EMenuEntry.ITEM,
            title: 'World',
            link: resolveRoutePath(namedRoutes.KeystoneTeamRankingsMdiSpring2019, { region: 'world' })
          },
          {
            type: EMenuEntry.DIVIDER
          },
          ...TOURNAMENT_REGIONS_NO_CHINA.slice(1).map((region: any) => ({
            type: EMenuEntry.ITEM,
            title: region.name,
            link: resolveRoutePath(namedRoutes.KeystoneTeamRankingsMdiSpring2019, { region: region.slug })
          }))
        ]
      }
    ]
  },

  // MDI Blizzcon ALl-Stars
  {
    type: EMenuEntry.ITEM,
    title: 'MDI BlizzCon All-Stars (2018)',
    link: '/mythic-dungeon-invitational-allstars-2018/overview',
    children: [
      {
        type: EMenuEntry.ITEM,
        title: 'Overview',
        link: '/mythic-dungeon-invitational-allstars-2018/overview'
      },
      {
        type: EMenuEntry.ITEM,
        title: 'All-Stars Bracket',
        link: '/mythic-dungeon-invitational-allstars-2018/bracket'
      }
    ]
  },

  // Mythic Dungeon Invitational (2018)
  {
    type: EMenuEntry.ITEM,
    title: 'Mythic Dungeon Invitational (2018)',
    link: '/mythic-dungeon-invitational-2018',
    children: [
      {
        type: EMenuEntry.ITEM,
        title: 'Standings',
        link: '/mythic-dungeon-invitational-2018/standings'
      },
      {
        type: EMenuEntry.ITEM,
        title: 'Global Group Stage',
        link: '/mythic-dungeon-invitational-2018/global-group-stage'
      },
      {
        type: EMenuEntry.ITEM,
        title: 'Regional Group Stage',
        link: '/mythic-dungeon-invitational-2018/regional-group-stage',
        children: [
          {
            type: EMenuEntry.ITEM,
            title: 'All Regions',
            link: '/mythic-dungeon-invitational-2018/regional-group-stage'
          },
          {
            type: EMenuEntry.DIVIDER
          },

          ...TOURNAMENT_REGIONS.slice(1).map((region: any) => ({
            type: EMenuEntry.ITEM,
            title: region.name,
            link: `/mythic-dungeon-invitational-2018/brackets/regional-group-stage-${region.slug}`
          }))
        ]
      },
      {
        type: EMenuEntry.ITEM,
        title: 'nav:mdi.time-trials',
        link: resolveRoutePath(namedRoutes.MythicDungeonInvitational2018TimeTrialsOverview, {
          noHash: true,
          region: 'world'
        }),
        children: [
          {
            type: EMenuEntry.ITEM,
            title: 'nav:mdi.dungeon-leaderboards',
            link: resolveRoutePath(namedRoutes.MythicDungeonInvitational2018TimeTrialsOverview, {
              region: 'world'
            }),
            children: [
              {
                type: EMenuEntry.ITEM,
                title: 'World',
                link: resolveRoutePath(namedRoutes.MythicDungeonInvitational2018TimeTrialsOverview, {
                  region: 'world'
                })
              },
              {
                type: EMenuEntry.DIVIDER
              },
              ...TOURNAMENT_REGIONS.slice(1).map((region: any) => ({
                type: EMenuEntry.ITEM,
                title: region.name,
                link: resolveRoutePath(namedRoutes.MythicDungeonInvitational2018TimeTrialsOverview, {
                  region: region.slug
                })
              }))
            ]
          },
          {
            type: EMenuEntry.ITEM,
            title: 'nav:mdi.team-leaderboards',
            link: resolveRoutePath(namedRoutes.KeystoneTimeTrialsTeamRankingsMdi2018, {
              noHash: true,
              region: 'world'
            }),
            children: [
              {
                type: EMenuEntry.ITEM,
                title: 'World',
                link: resolveRoutePath(namedRoutes.KeystoneTimeTrialsTeamRankingsMdi2018, { region: 'world' })
              },
              {
                type: EMenuEntry.DIVIDER
              },
              ...TOURNAMENT_REGIONS.slice(1).map((region: any) => ({
                type: EMenuEntry.ITEM,
                title: region.name,
                link: resolveRoutePath(namedRoutes.KeystoneTimeTrialsTeamRankingsMdi2018, { region: region.slug })
              }))
            ]
          },
          {
            type: EMenuEntry.DIVIDER
          },
          {
            type: EMenuEntry.ITEM,
            title: 'Preparation',
            link: resolveRoutePath(namedRoutes.MythicDungeonInvitational2018TimeTrialsPreparation)
          }
        ]
      }
    ]
  },

  // Mythic Dungeon Invitational (2017)
  {
    type: EMenuEntry.ITEM,
    title: 'Mythic Dungeon Invitational (2017)',
    link: resolveRoutePath(namedRoutes.MythicDungeonInvitational2017Landing),
    children: [
      {
        type: EMenuEntry.ITEM,
        title: 'Proving Grounds',
        link: resolveRoutePath(namedRoutes.KeystoneTeamRankingsMdi2017, {
          region: 'world'
        }),
        children: [
          {
            type: EMenuEntry.ITEM,
            title: TOURNAMENT_REGIONS[0].name,
            link: resolveRoutePath(namedRoutes.KeystoneTeamRankingsMdi2017, {
              region: TOURNAMENT_REGIONS[0].slug
            })
          },
          {
            type: EMenuEntry.DIVIDER
          },
          ...TOURNAMENT_REGIONS.slice(1).map((region: any) => ({
            type: EMenuEntry.ITEM,
            title: region.name,
            link: resolveRoutePath(namedRoutes.KeystoneTeamRankingsMdi2017, { region: region.slug })
          }))
        ]
      },
      {
        type: EMenuEntry.ITEM,
        title: 'Regionals & Finals',
        link: resolveRoutePath(namedRoutes.MythicDungeonInvitational2017Landing)
      }
    ]
  }
];

function getCommunityEventMenuItems(): TMenuEntry[] {
  return [
    {
      type: EMenuEntry.ITEM,
      title: t('common:expansions.shadowlands.name'),
      children: [
        {
          type: EMenuEntry.ITEM,
          title: t('nav:mythic-plus.events.break-the-meta.title', {
            expansionAbreviation: t('common:expansions.shadowlands.short-name'),
            seasonNumber: t('common:season-x', { num: 4 })
          }),
          link: '/mythic-plus-rankings/season-sl-4-break-the-meta/all/world/leaderboards-strict',
          children: [
            {
              type: EMenuEntry.ITEM,
              title: t('nav:mythic-plus.events.break-the-meta.menu-item-children.runs-leaderboard'),
              link: '/mythic-plus-rankings/season-sl-4-break-the-meta/all/world/leaderboards-strict',
              wowIcon: 'spell_holy_borrowedtime'
            },
            {
              type: EMenuEntry.DIVIDER
            },
            {
              type: EMenuEntry.ITEM,
              title: t('nav:mythic-plus.events.break-the-meta.menu-item-children.dps-leaderboards'),
              link: '/mythic-plus-character-rankings/season-sl-4-break-the-meta/world/all/dps',
              iconClassName: 'icon-role icon-x-small dps-lfg',
              children: [
                ['affliction', 'warlock'],
                ['arcane', 'mage'],
                ['arms', 'warrior'],
                ['assassination', 'rogue'],
                ['balance', 'druid'],
                ['beast-mastery', 'hunter'],
                ['elemental', 'shaman'],
                ['enhancement', 'shaman'],
                ['feral', 'druid'],
                ['frost', 'death-knight'],
                ['frost', 'mage'],
                ['havoc', 'demon-hunter'],
                ['retribution', 'paladin'],
                ['shadow', 'priest'],
                ['subtlety', 'rogue'],
                ['unholy', 'death-knight']
              ].map(([specSlug, classSlug]: string[]) => ({
                type: EMenuEntry.ITEM,
                link: `/mythic-plus-spec-rankings/season-sl-4-break-the-meta/world/${classSlug}/${specSlug}`,
                customIconComponent: createElement(SpecIcon, { class: classSlug, spec: specSlug }),
                className: `class-color--${classSlug}`,
                title: `${t(`game:specs.${classSlug}.${specSlug}.name`)} ${t(`game:classes.${classSlug}.name`)}`
              }))
            },
            {
              type: EMenuEntry.ITEM,
              title: t('nav:mythic-plus.events.break-the-meta.menu-item-children.healer-leaderboards'),
              link: '/mythic-plus-character-rankings/season-sl-4-break-the-meta/world/all/healer',
              iconClassName: 'icon-role icon-x-small healer-lfg',
              children: [
                ['discipline', 'priest'],
                ['holy', 'paladin'],
                ['mistweaver', 'monk']
              ].map(([specSlug, classSlug]: string[]) => ({
                type: EMenuEntry.ITEM,
                link: `/mythic-plus-spec-rankings/season-sl-4-break-the-meta/world/${classSlug}/${specSlug}`,
                customIconComponent: createElement(SpecIcon, { class: classSlug, spec: specSlug }),
                className: `class-color--${classSlug}`,
                title: `${t(`game:specs.${classSlug}.${specSlug}.name`)} ${t(`game:classes.${classSlug}.name`)}`
              }))
            },
            {
              type: EMenuEntry.ITEM,
              title: t('nav:mythic-plus.events.break-the-meta.menu-item-children.tank-leaderboards'),
              link: '/mythic-plus-character-rankings/season-sl-4-break-the-meta/world/all/tank',
              iconClassName: 'icon-role icon-x-small tank-lfg',
              children: [
                ['guardian', 'druid'],
                ['protection', 'warrior'],
                ['vengeance', 'demon-hunter']
              ].map(([specSlug, classSlug]: string[]) => ({
                type: EMenuEntry.ITEM,
                link: `/mythic-plus-spec-rankings/season-sl-4-break-the-meta/world/${classSlug}/${specSlug}`,
                customIconComponent: createElement(SpecIcon, { class: classSlug, spec: specSlug }),
                className: `class-color--${classSlug}`,
                title: `${t(`game:specs.${classSlug}.${specSlug}.name`)} ${t(`game:classes.${classSlug}.name`)}`
              }))
            },
            {
              type: EMenuEntry.DIVIDER
            },
            {
              type: EMenuEntry.ITEM,
              title: t('nav:mythic-plus.events.break-the-meta.menu-item-children.announcement'),
              link: '/news/403-break-the-meta-s4-arrives-october-4th',
              wowIcon: 'inv_misc_horn_04'
            }
          ]
        },
        {
          type: EMenuEntry.ITEM,
          title: t('nav:mythic-plus.events.afdc.title', { expansionAbreviation: 'SL', seasonNumber: 4 }),
          link: '/afdc/season-sl-4/tournament-teams',
          children: [
            {
              type: EMenuEntry.ITEM,
              title: 'nav:mythic-plus.events.afdc.team-leaderboards',
              link: '/afdc/season-sl-4/tournament-teams'
            },
            {
              type: EMenuEntry.ITEM,
              title: 'nav:mythic-plus.events.afdc.dungeon-leaderboards',
              link: '/afdc/season-sl-4/tournament-dungeons'
            }
          ]
        },
        {
          type: EMenuEntry.ITEM,
          title: t('nav:mythic-plus.events.charity-pro-am.title-year', { year: '2022' }),
          link: '/charity-pro-am-2022/teams',
          children: [
            {
              type: EMenuEntry.ITEM,
              title: 'nav:mdi.team-leaderboards',
              link: '/charity-pro-am-2022/teams'
            },
            {
              type: EMenuEntry.ITEM,
              title: 'nav:mdi.dungeon-leaderboards',
              link: '/charity-pro-am-2022/dungeons'
            }
          ]
        },
        {
          type: EMenuEntry.ITEM,
          title: t('nav:mythic-plus.events.afdc.title', { expansionAbreviation: 'SL', seasonNumber: 3 }),
          link: '/afdc/season-sl-3/tournament-teams',
          children: [
            {
              type: EMenuEntry.ITEM,
              title: 'nav:mythic-plus.events.afdc.team-leaderboards',
              link: '/afdc/season-sl-3/tournament-teams'
            },
            {
              type: EMenuEntry.ITEM,
              title: 'nav:mythic-plus.events.afdc.dungeon-leaderboards',
              link: '/afdc/season-sl-3/tournament-dungeons'
            }
          ]
        },
        {
          type: EMenuEntry.ITEM,
          title: 'nav:mythic-plus.events.class-clash-sl3',
          link: '/mythic-plus-rankings/season-sl-3-event-class-clash/all/world/leaderboards-strict'
        },
        {
          type: EMenuEntry.ITEM,
          title: t('nav:mythic-plus.events.legion-timewalking-event.title'),
          link: '/mythic-plus-rankings/season-sl-2-legion-timewalking/all/world/leaderboards',
          children: [
            {
              type: EMenuEntry.ITEM,
              title: t('nav:mythic-plus.events.legion-timewalking-event.menu-item-children.announcement'),
              link: '/news/320-legion-timewalking-mythic-plus-events'
            },
            {
              type: EMenuEntry.DIVIDER
            },
            {
              type: EMenuEntry.ITEM,
              title: t('nav:mythic-plus.events.legion-timewalking-event.menu-item-children.all-runs'),
              link: '/mythic-plus-rankings/season-sl-2-legion-timewalking/all/world/leaderboards'
            },
            {
              type: EMenuEntry.ITEM,
              title: t('nav:mythic-plus.events.legion-timewalking-event.menu-item-children.timed-runs'),
              link: '/mythic-plus-rankings/season-sl-2-legion-timewalking/all/world/leaderboards-strict'
            },
            {
              type: EMenuEntry.ITEM,
              title: t('nav:mythic-plus.events.legion-timewalking-event.menu-item-children.week-x-leaderboards', {
                week: 1
              }),
              link: '/mythic-plus-affix-rankings/season-sl-2-legion-timewalking/all/world/leaderboards-strict/tyrannical',
              children: [
                {
                  type: EMenuEntry.ITEM,
                  title: t('nav:mythic-plus.events.legion-timewalking-event.misc.high-key-hustle-affix', {
                    affix: t('affixes:9.name')
                  }),
                  link: '/mythic-plus-affix-rankings/season-sl-2-legion-timewalking/all/world/leaderboards-strict/tyrannical'
                },
                {
                  type: EMenuEntry.ITEM,
                  title: t('nav:mythic-plus.events.legion-timewalking-event.misc.three-players'),
                  link: '/mythic-plus-rankings/season-sl-2-legion-timewalking-event-3-players/all/world/leaderboards'
                },
                {
                  type: EMenuEntry.ITEM,
                  title: t('nav:mythic-plus.events.legion-timewalking-event.misc.five-of-one-class'),
                  link: '/mythic-plus-rankings/season-sl-2-legion-timewalking-event-5-same-class/all/world/leaderboards'
                }
              ]
            },
            {
              type: EMenuEntry.ITEM,
              title: t('nav:mythic-plus.events.legion-timewalking-event.menu-item-children.week-x-leaderboards', {
                week: 2
              }),
              link: '/mythic-plus-affix-rankings/season-sl-2-legion-timewalking/all/world/leaderboards-strict/fortified',
              children: [
                {
                  type: EMenuEntry.ITEM,
                  title: t('nav:mythic-plus.events.legion-timewalking-event.misc.high-key-hustle-affix', {
                    affix: t('affixes:10.name')
                  }),
                  link: '/mythic-plus-affix-rankings/season-sl-2-legion-timewalking/all/world/leaderboards-strict/fortified'
                },
                {
                  type: EMenuEntry.ITEM,
                  title: t('nav:mythic-plus.events.legion-timewalking-event.misc.two-players'),
                  link: '/mythic-plus-rankings/season-sl-2-legion-timewalking-event-2-players/all/world/leaderboards'
                },
                {
                  type: EMenuEntry.ITEM,
                  title: t('nav:mythic-plus.events.legion-timewalking-event.misc.five-of-one-class'),
                  link: '/mythic-plus-rankings/season-sl-2-legion-timewalking-event-4-same-class/all/world/leaderboards'
                }
              ]
            }
          ]
        },
        {
          type: EMenuEntry.ITEM,
          title: t('nav:mythic-plus.events.keystone-builders.title'),
          link: '/mythic-plus-rankings/season-sl-2-event-keystone-builders/all/world/leaderboards-strict',
          children: [
            {
              type: EMenuEntry.ITEM,
              title: t('nav:mythic-plus.events.keystone-builders.menu-item-children.all-runs'),
              link: '/mythic-plus-rankings/season-sl-2-event-keystone-builders/all/world/leaderboards-strict'
            },
            {
              type: EMenuEntry.ITEM,
              title: t('nav:mythic-plus.events.keystone-builders.menu-item-children.teams'),
              link: '/keystone-builders/season-2/team-rankings'
            }
          ]
        },
        {
          type: EMenuEntry.ITEM,
          title: t('nav:mythic-plus.events.break-the-meta.title', {
            expansionAbreviation: t('common:expansions.shadowlands.short-name'),
            seasonNumber: t('common:season-x', { num: 2 })
          }),
          link: '/mythic-plus-rankings/season-sl-2-event-break-the-meta/all/world/leaderboards-strict'
        },
        {
          type: EMenuEntry.ITEM,
          title: t('nav:mythic-plus.events.bloodlust-tournament.title'),
          children: [
            {
              type: EMenuEntry.ITEM,
              title: t('nav:mythic-plus.events.bloodlust-tournament.bmt-year', { year: 2021 }),
              link: resolveRoutePath(namedRoutes.BloodlustSLBracket2021, { bracket: 'playoffs' })
            },
            {
              type: EMenuEntry.ITEM,
              title: t('nav:mythic-plus.events.bloodlust-tournament.bmt-year', { year: 2020 }),
              link: resolveRoutePath(namedRoutes.BloodlustBFABracket2020, { bracket: 'playoffs' })
            }
          ]
        }
      ]
    },
    {
      type: EMenuEntry.ITEM,
      title: t('common:expansions.battle-for-azeroth.name'),
      children: [
        {
          type: EMenuEntry.ITEM,
          title: t('nav:mythic-plus.events.charity-pro-am.title'),
          link: resolveRoutePath(namedRoutes.CharityProAm2021, { bracket: 'season-sl-1' })
        },
        {
          type: EMenuEntry.ITEM,
          title: t('nav:mythic-plus.events.ksm-experiments.title'),
          link: resolveRoutePath(namedRoutes.Ksm2020FXBracketsPage, { bracket: 'format-experiments-2020' }),
          children: [
            {
              type: EMenuEntry.ITEM,
              title: t('nav:mythic-plus.events.ksm-experiments.menu-item-children.tournament-bracket'),
              link: resolveRoutePath(namedRoutes.Ksm2020FXBracketsPage, { bracket: 'format-experiments-2020' })
            },
            {
              type: EMenuEntry.ITEM,
              title: t('nav:mythic-plus.events.ksm-experiments.menu-item-children.time-trials-leaderboard'),
              link: namedRoutes.Ksm2020FXTimeTrialsLeaderboardPage
            },
            {
              type: EMenuEntry.ITEM,
              title: t('nav:mythic-plus.events.ksm-experiments.menu-item-children.time-trials-teams'),
              link: resolveRoutePath(namedRoutes.Ksm2020FXTimeTrialsTeamLeaderboardPage, {})
            }
          ]
        },
        {
          type: EMenuEntry.ITEM,
          title: t('nav:mythic-plus.events.dream-masters.title', { seasonNumber: t('common:season-x', { num: 2 }) }),
          children: [
            {
              type: EMenuEntry.ITEM,
              title: t('nav:mythic-plus.events.dream-masters.menu-item-children.announcement'),
              link: '/news/112-dream-mythic-masters-season-2'
            },
            {
              type: EMenuEntry.ITEM,
              title: t('nav:mythic-plus.events.dream-masters.menu-item-children.time-trials-leaderboard'),
              link: resolveRoutePath(namedRoutes.DreamGaming2020TimeTrialsLeaderboardPage, {
                leaderboard: 'dream-mythic-masters-2020'
              })
            },
            {
              type: EMenuEntry.ITEM,
              title: t('nav:mythic-plus.events.dream-masters.menu-item-children.time-trials-teams'),
              link: resolveRoutePath(namedRoutes.DreamGaming2020TimeTrialsTeamLeaderboardPage, {
                leaderboard: 'dream-mythic-masters-2020'
              })
            },
            {
              type: EMenuEntry.ITEM,
              title: t('nav:mythic-plus.events.dream-masters.menu-item-children.tournament-bracket'),
              link: resolveRoutePath(namedRoutes.DreamGaming2020BracketsPage, {
                bracket: 'playoffs'
              })
            }
          ]
        },
        {
          type: EMenuEntry.ITEM,
          title: t('nav:mythic-plus.events.ksm-fling.title', { year: 2020 }),
          children: [
            {
              type: EMenuEntry.ITEM,
              title: t('nav:mythic-plus.events.ksm-experiments.menu-item-children.time-trials-leaderboard'),
              link: namedRoutes.Ksm2020TimeTrialsLeaderboardPage
            },
            {
              type: EMenuEntry.ITEM,
              title: t('nav:mythic-plus.events.ksm-experiments.menu-item-children.time-trials-teams'),
              link: namedRoutes.Ksm2020TimeTrialsTeamLeaderboardPage
            },
            {
              type: EMenuEntry.ITEM,
              title: t('nav:mythic-plus.events.ksm-experiments.menu-item-children.tournament-bracket'),
              link: resolveRoutePath(namedRoutes.Ksm2020BracketsPage, { bracket: 'winterspring-fling-2020' })
            }
          ]
        },
        {
          type: EMenuEntry.ITEM,
          title: t('nav:mythic-plus.events.dream-tournament.title', { year: 2019 }),
          link: '/dream-mythic-tournament/2019/playoffs'
        }
      ]
    },
    {
      type: EMenuEntry.ITEM,
      title: t('common:expansions.legion.name'),
      children: [
        {
          type: EMenuEntry.ITEM,
          title: t('nav:mythic-plus.events.tespa.title', { year: 2017 }),
          link: resolveRoutePath(namedRoutes.CollegiateDungeonRaceLanding)
        }
      ]
    }
  ];
}

function getExtrasMenus(props: any) {
  const menus: TMenuEntry[] = [
    ...(props.currentFeatures.tww_review
      ? ([
          {
            type: EMenuEntry.ITEM,
            title: t('nav:community.recaps.expansion-recap', {
              expansion: t('common:expansions.the-war-within.name')
            }),
            wowIcon: 'inv_112_achievement_zone_karesh',
            className: 'text-raiderio text-bold',
            link: '/tww-review'
          },
          {
            type: EMenuEntry.DIVIDER
          }
        ] as TMenuEntry[])
      : []),

    ...(props.currentFeatures.dragonflight_review && !props.currentFeatures.tww1_review
      ? ([
          {
            type: EMenuEntry.ITEM,
            title: t('nav:community.recaps.expansion-recap', {
              expansion: t('common:expansions.dragonflight.name')
            }),
            wowIcon: 'inv_misc_token_boost_dragonflight',
            className: 'text-raiderio text-bold',
            link: '/dragonflight-review'
          },
          {
            type: EMenuEntry.DIVIDER
          }
        ] as TMenuEntry[])
      : []),

    // <MenuItem
    //   key="update_now"
    //   className="text-bold"
    //   itemIcon={<i className="fas fa-arrow-up-to-arc text-bold" />}
    // ></MenuItem>
    {
      type: EMenuEntry.ITEM,
      title: 'nav:community.update-character-or-guild',
      link: 'update_now',
      iconClassName: 'fas fa-arrow-up-to-arc'
    },
    // {
    //   type: EMenuEntry.ITEM,
    //   title: 'nav:community.discord-alerts',
    //   link: '/discord',
    //   iconClassName: 'fa-brands fa-discord'
    // },
    {
      type: EMenuEntry.ITEM,
      title: 'nav:community.stream-widgets',
      link: namedRoutes.Widget,
      iconClassName: 'fa-brands fa-twitch'
    },
    {
      type: EMenuEntry.ITEM,
      title: 'nav:main.guides',
      iconClassName: 'fa fa-book',
      children: GUIDES_MENU_ITEMS
    },
    {
      type: EMenuEntry.ITEM,
      title: 'nav:community.featured-streamers',
      link: '/news/322-featured-streamers',
      iconClassName: 'fa fa-person-rays'
    },
    {
      type: EMenuEntry.ITEM,
      title: 'nav:community.transmog',
      link: '/transmogs',
      iconClassName: 'fa fa-hat-witch'
    },

    {
      type: EMenuEntry.ITEM,
      title: 'nav:community.raiderio-recaps',
      iconClassName: 'fa fa-page-caret-down',
      children: [
        ...(props.currentFeatures.tww2_review
          ? [
              {
                type: EMenuEntry.ITEM,
                title: t('nav:community.recaps.season-recap', {
                  expansion: t('common:expansions.the-war-within.name'),
                  season: t('seasons:season-tww-2.base-name')
                }),
                link: '/the-war-within-season-2-recap'
              }
            ]
          : []),
        ...(props.currentFeatures.tww1_review
          ? [
              {
                type: EMenuEntry.ITEM,
                title: t('nav:community.recaps.season-recap', {
                  expansion: t('common:expansions.the-war-within.name'),
                  season: t('seasons:season-tww-1.base-name')
                }),
                link: '/the-war-within-season-1-recap'
              }
            ]
          : []),

        ...(props.currentFeatures.tww_review
          ? [
              {
                type: EMenuEntry.ITEM,
                title: t('nav:community.recaps.full-expansion-recap', {
                  expansion: t('common:expansions.the-war-within.name')
                }),
                link: '/tww-review'
              }
            ]
          : []),

        ...(props.currentFeatures.dragonflight_review
          ? [
              {
                type: EMenuEntry.ITEM,
                title: t('nav:community.recaps.full-expansion-recap', {
                  expansion: t('common:expansions.dragonflight.name')
                }),
                link: '/dragonflight-review'
              }
            ]
          : []),

        ...(props.currentFeatures.df3_review
          ? [
              {
                type: EMenuEntry.ITEM,
                title: t('nav:community.recaps.season-recap', {
                  expansion: t('common:expansions.dragonflight.name'),
                  season: t('seasons:season-df-3.base-name')
                }),
                link: '/dragonflight-season-3-recap'
              }
            ]
          : []),

        ...(props.currentFeatures.df2_review
          ? [
              {
                type: EMenuEntry.ITEM,
                title: t('nav:community.recaps.season-recap', {
                  expansion: t('common:expansions.dragonflight.name'),
                  season: t('seasons:season-df-2.base-name')
                }),
                link: '/dragonflight-season-2-recap'
              }
            ]
          : []),

        ...(props.currentFeatures.df1_review
          ? [
              {
                type: EMenuEntry.ITEM,
                title: t('nav:community.recaps.season-recap', {
                  expansion: t('common:expansions.dragonflight.name'),
                  season: t('seasons:season-df-1.base-name')
                }),
                link: '/dragonflight-season-1-recap'
              }
            ]
          : []),

        ...(props.currentFeatures.shadowlands_review
          ? [
              {
                type: EMenuEntry.ITEM,
                title: t('nav:community.recaps.full-expansion-recap', {
                  expansion: t('common:expansions.shadowlands.name')
                }),
                link: '/shadowlands-review'
              }
            ]
          : []),

        ...(props.currentFeatures.sl3_review
          ? [
              {
                type: EMenuEntry.ITEM,
                title: t('nav:community.recaps.season-recap', {
                  expansion: t('common:expansions.shadowlands.name'),
                  season: t('seasons:season-sl-3.base-name')
                }),
                link: '/sl3-review'
              }
            ]
          : []),

        ...(props.currentFeatures.sl2_review
          ? [
              {
                type: EMenuEntry.ITEM,
                title: t('nav:community.recaps.season-recap', {
                  expansion: t('common:expansions.shadowlands.name'),
                  season: t('seasons:season-sl-2.base-name')
                }),
                link: '/sl2-review'
              }
            ]
          : []),

        {
          type: EMenuEntry.ITEM,
          title: t('nav:community.recaps.season-recap', {
            expansion: t('common:expansions.shadowlands.name'),
            season: t('seasons:season-sl-1.base-name')
          }),
          link: '/sl1-review'
        },
        {
          type: EMenuEntry.ITEM,
          title: 'nav:community.recaps.expansion-bfa',
          link: '/bfa-review'
        },
        {
          type: EMenuEntry.ITEM,
          title: 'nav:community.recaps.year-in-review-2018',
          link: '/year-in-review-2018'
        },
        {
          type: EMenuEntry.ITEM,
          title: 'nav:community.recaps.year-in-review-2017',
          link: '/year-in-review-2017'
        }
      ]
    },
    {
      type: EMenuEntry.DIVIDER
    },
    {
      type: EMenuEntry.ITEM,
      title: 'nav:community.raidleio-game',
      link: '/raidle',
      iconClassName: 'fa fa-keyboard'
    },
    {
      type: EMenuEntry.ITEM,
      title: 'nav:community.shop',
      link: 'https://shop.raider.io',
      iconClassName: 'fa fa-shirt'
    },
    {
      type: EMenuEntry.DIVIDER
    },
    {
      type: EMenuEntry.ITEM,
      title: 'nav:community.faq',
      link: 'https://support.raider.io',
      iconClassName: 'fa fa-info-circle'
    },
    ...(props.currentFeatures[FeatureFlag.MODERATION_REPORTING_ENABLED]
      ? ([
          {
            type: EMenuEntry.DIVIDER
          },
          {
            type: EMenuEntry.ITEM,
            title: 'moderation:button.report',
            iconClassName: 'fa fa-flag',
            onClick: () => openGlobalReportModal()
          }
        ] as TMenuEntry[])
      : [])
  ];
  return menus;
}

function getRealmsMenuItems(props: any) {
  const sortedSubRegions: TSubRegionResource[] = props.subRegions.sort((a: any, b: any) => {
    if (a.name < b.name) {
      return -1;
    } else if (a.name > b.name) {
      return 1;
    }
    return 0;
  });

  const REALMS_MENU_ITEMS: TMenuEntry[] = [
    {
      type: EMenuEntry.ITEM,
      title: 'nav:all-realms',
      link: resolveRoutePath(namedRoutes.ConnectedRealms, {})
    },
    {
      type: EMenuEntry.DIVIDER
    },
    ...['us', 'eu', 'kr', 'tw', 'cn']
      .map((regionSlug) => ({
        name: `regions:${regionSlug}.name`,
        slug: regionSlug
      }))
      .map<TMenuEntry>((regionInfo) => ({
        type: EMenuEntry.ITEM,
        title: regionInfo.name,
        link: resolveRoutePath(namedRoutes.ConnectedRealms, { region: regionInfo.slug }),
        children: undefinedIfEmptyArray(
          sortedSubRegions
            .filter((sr) => sr.regions.length === 1 && sr.regions.find((r) => r.slug === regionInfo.slug))
            .map<TMenuEntry>((sr) => ({
              type: EMenuEntry.ITEM,
              title: t(`regions:${sr.slug}.name`),
              link: resolveRoutePath(namedRoutes.ConnectedRealms, { region: sr.slug })
            }))
        )
      })),
    {
      type: EMenuEntry.DIVIDER
    },
    ...sortedSubRegions
      .filter((sr) => sr.regions.length > 1)
      .map<TMenuEntry>((sr) => ({
        type: EMenuEntry.ITEM,
        title: t(`regions:${sr.slug}.name`),
        link: resolveRoutePath(namedRoutes.ConnectedRealms, { region: sr.slug })
      }))
  ];

  return REALMS_MENU_ITEMS;
}

// Can't have this be a React component because rc-menu passes in special props to child menus
function getRaidSubMenuItems(props: any) {
  const { raid } = props;
  const menuItems: TMenuEntry[] = [
    {
      type: EMenuEntry.ITEM,
      title: `raids:${raid.slug}.name`,
      customIcon: asset(raid.icon_url),
      link: `/${raid.slug}/rankings/world/${props.difficulty}`,
      children: [
        {
          type: EMenuEntry.ITEM,
          title: 'nav:raids.raid-rankings',
          link: resolveRoutePath(
            namedRoutes.RaidRankings,
            buildRouteParams(props.match.params, {
              raid: raid.slug,
              region: props.region,
              difficulty: props.difficulty
            })
          )
        },
        {
          type: EMenuEntry.ITEM,
          title: 'nav:raids.boss-rankings',
          children: raid.encounters.map((boss: any) => ({
            type: EMenuEntry.ITEM,
            title: `raids:${raid.slug}.bosses.${boss.slug}.name`,
            customIcon: asset(boss.iconUrl),
            link: resolveRoutePath(
              namedRoutes.BossRankings,
              buildRouteParams(props.match.params, {
                raid: raid.slug,
                boss: boss.slug,
                region: props.region,
                difficulty: props.difficulty
              })
            )
          }))
        },
        {
          type: EMenuEntry.ITEM,
          title: 'nav:raids.hall-of-fame',
          link: resolveRoutePath(
            namedRoutes.RaidHallOfFame,
            buildRouteParams(props.match.params, {
              raid: raid.slug,
              region: props.region,
              difficulty: props.difficulty
            })
          )
        },
        ...(raidSupportsWorldFirstTracker(raid.slug)
          ? ([
              {
                type: EMenuEntry.ITEM,
                title: 'nav:raids.regional-race-tracker',
                link: resolveRoutePath(
                  namedRoutes.RaidWorldFirstTracker,
                  buildRouteParams(props.match.params, {
                    raid: raid.slug,
                    region: props.region,
                    difficulty: props.difficulty
                  })
                )
              }
            ] as any) // TODO: type fix - should be TMenuEntry[]
          : []),
        {
          type: EMenuEntry.DIVIDER
        },
        ...(raidSupportsGlobalCoverage(raid.slug)
          ? ([
              {
                type: EMenuEntry.ITEM,
                title: 'nav:raids.rwf-global-coverage',
                link: resolveRoutePath(
                  namedRoutes.RaidGlobalCoverage,
                  buildRouteParams(props.match.params, {
                    raid: RAID_METADATA_BY_SLUG[raid.slug]?.globalCoverageSlug || raid.slug
                  })
                )
              }
            ] as TMenuEntry[])
          : []),
        ...(WATCH_GUILDS_BY_RAID[raid.slug]
          ? ([
              {
                type: EMenuEntry.ITEM,
                title: 'nav:raids.rwf-analyzer',
                link: resolveRoutePath(
                  namedRoutes.RWFAnalyzerPage,
                  buildRouteParams(props.match.params, {
                    raid: raid.slug
                  })
                )
              }
            ] as TMenuEntry[])
          : [])
      ]
    }
  ];
  return menuItems;
}

export function getMenuItems(props: any) {
  let foundRaid = null;
  const relevantRaids: any[] = [];
  const otherRaids: any[] = [];

  for (const raid of props.raids) {
    if (isCurrentExpansionRaid(raid.slug)) {
      relevantRaids.push(raid);
    } else {
      otherRaids.push(raid);
    }
    if (!foundRaid && raid.slug === props.match.params.raid) {
      foundRaid = raid;
    }
  }

  const primaryRaid = _.first(relevantRaids);
  const currentRaid = foundRaid || primaryRaid;

  const RWF_ACTIVE_MENU: TMenuEntry[] = [
    {
      type: EMenuEntry.ITEM,
      link: resolveRoutePath(namedRoutes.RaidGlobalCoverage, {
        raid:
          RAID_METADATA_BY_SLUG[props.nextRaidSlug || props.currentRaidSlug]?.globalCoverageSlug ||
          props.nextRaidSlug ||
          props.currentRaidSlug
      }),
      children: [
        {
          type: EMenuEntry.ITEM,
          link: resolveRoutePath(namedRoutes.RaidGlobalCoverage, {
            raid:
              RAID_METADATA_BY_SLUG[props.nextRaidSlug || props.currentRaidSlug]?.globalCoverageSlug ||
              props.nextRaidSlug ||
              props.currentRaidSlug
          }),
          title: 'nav:raids.rwf-global-coverage'
        },
        ...(WATCH_GUILDS_BY_RAID[props.nextRaidSlug || props.currentRaidSlug]
          ? ([
              {
                type: EMenuEntry.ITEM,
                title: 'nav:raids.rwf-analyzer',
                link: resolveRoutePath(
                  namedRoutes.RWFAnalyzerPage,
                  buildRouteParams(props.match.params, {
                    raid: props.nextRaidSlug || props.currentRaidSlug
                  })
                )
              }
            ] as TMenuEntry[])
          : []),
        {
          type: EMenuEntry.ITEM,
          link: resolveRoutePath(namedRoutes.RaidRankings, {
            raid:
              RAID_METADATA_BY_SLUG[props.nextRaidSlug || props.currentRaidSlug]?.globalCoverageSlug ||
              props.nextRaidSlug ||
              props.currentRaidSlug,
            region: 'world',
            difficulty: 'mythic'
          }),
          title: 'nav:raids.raid-rankings'
        },
        {
          type: EMenuEntry.ITEM,
          link: resolveRoutePath(namedRoutes.RaidHallOfFame, {
            raid:
              RAID_METADATA_BY_SLUG[props.nextRaidSlug || props.currentRaidSlug]?.globalCoverageSlug ||
              props.nextRaidSlug ||
              props.currentRaidSlug,
            region: 'world',
            difficulty: 'mythic'
          }),
          title: 'nav:raids.hall-of-fame'
        },
        {
          type: EMenuEntry.DIVIDER
        },
        {
          type: EMenuEntry.ITEM,
          link: resolveRoutePath(namedRoutes.GuildBossProgressMonitoringTool, {}),
          title: 'RWF Guild Monitor',
          isPremium: true
        }
        // {
        //   type: EMenuEntry.ITEM,
        //   title: 'nav:raids.rwf-awards',
        //   link: '/events/rwf-awards/welcome-to-the-rwf-awards'
        // }
      ],
      title: 'nav:main.rwf',
      titleClass: 'text-bold',
      isLive: props.currentFeatures.rwf_live || false
    }
  ];

  const RWF_INACTIVE_MENU: TMenuEntry[] = [
    {
      type: EMenuEntry.ITEM,
      link: resolveRoutePath(namedRoutes.RaidGlobalCoverage, {
        raid:
          RAID_METADATA_BY_SLUG[props.nextRaidSlug || props.currentRaidSlug]?.globalCoverageSlug ||
          props.nextRaidSlug ||
          props.currentRaidSlug
      }),
      title: 'nav:raids.rwf-global-coverage',
      titleClass: 'text-bold',
      wowIcon: 'ability_racial_packhobgoblin',
      isLive: props.currentFeatures.rwf_live || false
    },
    {
      type: EMenuEntry.DIVIDER
    }
  ];

  const RAID_MENU: TMenuEntry[] = [
    {
      type: EMenuEntry.ITEM,
      title: 'nav:main.raids',
      titleClass: 'text-bold',
      // isDropdown: true,
      link: resolveRoutePath(namedRoutes.RaidRankings, {
        raid: currentRaid.slug,
        region: 'world',
        difficulty: gameConfig.RaidsBySlug[currentRaid.slug].supportedDifficulties[0] || 'mythic'
      }),
      children: () => [
        ...RWF_INACTIVE_MENU,

        ..._.flatMap(relevantRaids, (raid: any) =>
          getRaidSubMenuItems({
            match: props.match,
            raid,
            region: 'world',
            difficulty: gameConfig.RaidsBySlug[raid.slug].supportedDifficulties[0] || 'mythic'
          })
        ),
        {
          type: EMenuEntry.DIVIDER
        },
        {
          type: EMenuEntry.ITEM,
          title: 'nav:raid-tools.raid-tools',
          wowIcon: 'inv_misc_wrench_01',
          children: () => [
            ...(props.currentFeatures[FeatureFlag.RAID_COMPARISON_PAGE]
              ? [
                  {
                    type: EMenuEntry.ITEM,
                    title: 'composition-compare:compare-raid-comps',
                    link: namedRoutes.GroupCompositionCompare,
                    isNew: true,
                    wowIcon: 'spell_nzinsanity_dontlook'
                  }
                ]
              : []),
            {
              type: EMenuEntry.ITEM,
              link: resolveRoutePath(namedRoutes.GuildBossProgressMonitoringTool, {}),
              title: 'nav:raid-tools.guild-monitor-dashboard',
              isPremium: true,
              wowIcon: 'inv_111_gallywixcomputer_ventureco'
            }
          ]
        },
        {
          type: EMenuEntry.DIVIDER
        },
        {
          type: EMenuEntry.ITEM,
          title: 'nav:raids.older-raids',
          wowIcon: 'wow_token02',
          children: () =>
            _.flatMap(otherRaids, (raid: any) =>
              getRaidSubMenuItems({
                match: props.match,
                raid,
                region: 'world',
                difficulty: gameConfig.RaidsBySlug[raid.slug].supportedDifficulties[0] || 'mythic'
              })
            )
        }
      ]
    }
  ];

  const DF_SEASON_1_COMMUNITY_EVENTS: TMenuEntry[] = [
    {
      type: EMenuEntry.ITEM,
      title: t('nav:mythic-plus.events.break-the-meta.title', {
        expansionAbreviation: t('common:expansions.dragonflight.short-name'),
        seasonNumber: t('common:season-x', { num: 1 })
      }),
      link: '/mythic-plus-rankings/season-df-1-break-the-meta/all/world/leaderboards-strict',
      children: [
        {
          type: EMenuEntry.ITEM,
          title: t('nav:mythic-plus.events.break-the-meta.menu-item-children.runs-leaderboard'),
          link: '/mythic-plus-rankings/season-df-1-break-the-meta/all/world/leaderboards-strict',
          wowIcon: 'spell_holy_borrowedtime'
        },
        {
          type: EMenuEntry.DIVIDER
        },
        {
          type: EMenuEntry.ITEM,
          title: t('nav:mythic-plus.events.break-the-meta.menu-item-children.dps-leaderboards'),
          link: '/mythic-plus-character-rankings/season-df-1-break-the-meta/world/all/dps',
          iconClassName: 'icon-role icon-x-small dps-lfg',
          children: [
            ['affliction', 'warlock'],
            ['arcane', 'mage'],
            ['arms', 'warrior'],
            ['assassination', 'rogue'],
            ['devastation', 'evoker'],
            ['elemental', 'shaman'],
            ['feral', 'druid'],
            ['fire', 'mage'],
            ['frost', 'death-knight'],
            ['survival', 'hunter'],
            ['unholy', 'death-knight']
          ].map(([specSlug, classSlug]: string[]) => ({
            type: EMenuEntry.ITEM,
            link: `/mythic-plus-spec-rankings/season-df-1-break-the-meta/world/${classSlug}/${specSlug}`,
            customIconComponent: createElement(SpecIcon, { class: classSlug, spec: specSlug }),
            className: `class-color--${classSlug}`,
            title: `${t(`game:specs.${classSlug}.${specSlug}.name`)} ${t(`game:classes.${classSlug}.name`)}`
          }))
        },
        {
          type: EMenuEntry.ITEM,
          title: t('nav:mythic-plus.events.break-the-meta.menu-item-children.healer-leaderboards'),
          link: '/mythic-plus-character-rankings/season-df-1-break-the-meta/world/all/healer',
          iconClassName: 'icon-role icon-x-small healer-lfg',
          children: [
            ['discipline', 'priest'],
            ['holy', 'priest'],
            ['holy', 'paladin'],
            ['mistweaver', 'monk']
          ].map(([specSlug, classSlug]: string[]) => ({
            type: EMenuEntry.ITEM,
            link: `/mythic-plus-spec-rankings/season-df-1-break-the-meta/world/${classSlug}/${specSlug}`,
            customIconComponent: createElement(SpecIcon, { class: classSlug, spec: specSlug }),
            className: `class-color--${classSlug}`,
            title: `${t(`game:specs.${classSlug}.${specSlug}.name`)} ${t(`game:classes.${classSlug}.name`)}`
          }))
        },
        {
          type: EMenuEntry.ITEM,
          title: t('nav:mythic-plus.events.break-the-meta.menu-item-children.tank-leaderboards'),
          link: '/mythic-plus-character-rankings/season-df-1-break-the-meta/world/all/tank',
          iconClassName: 'icon-role icon-x-small tank-lfg',
          children: [
            ['guardian', 'druid'],
            ['brewmaster', 'monk'],
            ['vengeance', 'demon-hunter']
          ].map(([specSlug, classSlug]: string[]) => ({
            type: EMenuEntry.ITEM,
            link: `/mythic-plus-spec-rankings/season-df-1-break-the-meta/world/${classSlug}/${specSlug}`,
            customIconComponent: createElement(SpecIcon, { class: classSlug, spec: specSlug }),
            className: `class-color--${classSlug}`,
            title: `${t(`game:specs.${classSlug}.${specSlug}.name`)} ${t(`game:classes.${classSlug}.name`)}`
          }))
        },
        {
          type: EMenuEntry.DIVIDER
        },
        {
          type: EMenuEntry.ITEM,
          title: t('nav:mythic-plus.events.break-the-meta.menu-item-children.announcement'),
          link: '/news/493-break-the-meta-returns-for-dragonflight-season-1',
          wowIcon: 'inv_misc_horn_04'
        }
      ]
    },

    {
      type: EMenuEntry.ITEM,
      title: t('nav:mythic-plus.events.eve-ascension.title-year', { year: '2023' }),
      link: '/eve-ascension-2023/teams',
      children: [
        {
          type: EMenuEntry.ITEM,
          title: 'nav:mdi.team-leaderboards',
          link: '/eve-ascension-2023/teams'
        },
        {
          type: EMenuEntry.ITEM,
          title: 'nav:mdi.dungeon-leaderboards',
          link: '/eve-ascension-2023/dungeons'
        }
      ]
    },
    {
      type: EMenuEntry.ITEM,
      title: t('nav:mythic-plus.events.keystone-builders.title'),
      link: '/mythic-plus-rankings/season-df-1-event-keystone-builders/all/world/leaderboards-strict',
      children: [
        {
          type: EMenuEntry.ITEM,
          title: t('nav:mythic-plus.events.keystone-builders.finals'),
          link: '/keystone-builders/season-3-finals/teams',
          children: [
            {
              type: EMenuEntry.ITEM,
              title: t('nav:mythic-plus.events.keystone-builders.menu-item-children.teams'),
              link: '/keystone-builders/season-3-finals/teams'
            },
            {
              type: EMenuEntry.ITEM,
              title: t('nav:mythic-plus.events.keystone-builders.menu-item-children.all-runs'),
              link: '/keystone-builders/season-3-finals/dungeons'
            }
          ]
        },
        {
          type: EMenuEntry.ITEM,
          title: t('nav:mythic-plus.events.keystone-builders.qualifiers'),
          link: '/keystone-builders/season-3/team-rankings',
          children: [
            {
              type: EMenuEntry.ITEM,
              title: t('nav:mythic-plus.events.keystone-builders.menu-item-children.teams'),
              link: '/keystone-builders/season-3/team-rankings'
            },
            {
              type: EMenuEntry.ITEM,
              title: t('nav:mythic-plus.events.keystone-builders.menu-item-children.all-runs'),
              link: '/mythic-plus-rankings/season-df-1-event-keystone-builders/all/world/leaderboards-strict'
            }
          ]
        }
      ]
    },
    {
      type: EMenuEntry.ITEM,
      title: t('nav:mythic-plus.events.charity-pro-am.title-year', { year: '2023' }),
      link: '/charity-pro-am-2023/teams',
      children: [
        {
          type: EMenuEntry.ITEM,
          title: 'nav:mdi.team-leaderboards',
          link: '/charity-pro-am-2023/teams'
        },
        {
          type: EMenuEntry.ITEM,
          title: 'nav:mdi.dungeon-leaderboards',
          link: '/charity-pro-am-2023/dungeons'
        }
      ]
    },
    {
      type: EMenuEntry.ITEM,
      title: t('nav:mythic-plus.events.dungeon-dojo.title'),
      link: '/mythic-plus-rankings/season-df-1-event-dungeon-dojo-invitational/all/world/leaderboards-strict',
      children: [
        {
          type: EMenuEntry.ITEM,
          title: t('nav:mythic-plus.events.dungeon-dojo.menu-item-children.all-runs'),
          link: '/mythic-plus-rankings/season-df-1-event-dungeon-dojo-invitational/all/world/leaderboards-strict'
        },
        {
          type: EMenuEntry.ITEM,
          title: t('nav:mythic-plus.events.dungeon-dojo.menu-item-children.teams'),
          link: '/dungeon-dojo-invitational/season-1/team-rankings'
        }
      ]
    }
  ];

  const MYTHIC_PLUS_MENU: TMenuEntry[] = [
    {
      type: EMenuEntry.ITEM,
      title: 'nav:main.mythic-plus',
      titleClass: 'text-bold',
      link: resolveRoutePath(
        namedRoutes.KeystoneRunRankings,
        buildRouteParams(props.match.params, { type: 'leaderboards' })
      ),
      children: [
        ...(props.currentFeatures.event_proam_active
          ? ([
              {
                type: EMenuEntry.ITEM,
                title: 'LIVE NOW: Mythic+ Charity Pro-Am',
                link: '/charity-pro-am-2023/teams',
                className: 'text-raiderio text-bold',
                wowIcon: 'inv_misc_mdi_banner02'
              },
              {
                type: EMenuEntry.DIVIDER
              }
            ] as TMenuEntry[])
          : []),

        {
          type: EMenuEntry.ITEM,
          title: 'nav:mythic-plus.routes-and-guides.title',
          link: resolveRoutePath(namedRoutes.WeeklyRoutes),
          wowIcon: 'inv_misc_book_10',
          children: [
            {
              type: EMenuEntry.ITEM,
              title: 'nav:mythic-plus.routes-and-guides.weekly-route',
              link: resolveRoutePath(namedRoutes.WeeklyRoutes),
              wowIcon: 'inv_misc_paperbundle03a'
            },
            {
              type: EMenuEntry.ITEM,
              title: 'nav:mythic-plus.routes-and-guides.quick-boss-guides',
              link: 'https://youtube.com/playlist?list=PLTakbTpb-EBLi1NZ8THXLn3VpI1LtjWAn&si=K6528LiLzZzxJNPN',
              wowIcon: 'ability_stealth',
              postIconClass: 'fa fa-external-link slds-m-left--x-small'
            },
            {
              type: EMenuEntry.ITEM,
              title: () => t('nav:learn.expansion-affixes', { expansion: t('common:expansions.midnight.name') }),
              link: '/midnight-affixes',
              wowIcon: 'inv_achievement_raid_darkwell'
            },
            {
              type: EMenuEntry.DIVIDER
            },
            {
              type: EMenuEntry.ITEM,
              title: 'nav:mythic-plus.routes-and-guides.create-on-keystone-guru',
              link: 'https://keystone.guru/?utm_source=raiderio',
              wowIcon: 'inv_misc_map08'
            }
          ]
        },

        {
          type: EMenuEntry.DIVIDER
        },

        {
          type: EMenuEntry.ITEM,
          title: 'nav:mythic-plus.all-run-leaderboards',
          link: resolveRoutePath(
            namedRoutes.KeystoneRunRankings,
            buildRouteParams(props.match.params, { type: 'leaderboards' })
          ),
          wowIcon: 'wow_store'
        },
        {
          type: EMenuEntry.ITEM,
          title: 'nav:mythic-plus.timed-run-leaderboards',
          link: resolveRoutePath(
            namedRoutes.KeystoneRunRankings,
            buildRouteParams(props.match.params, { type: 'leaderboards-strict' })
          ),
          wowIcon: 'spell_holy_borrowedtime'
        },

        {
          type: EMenuEntry.ITEM,
          link: resolveRoutePath(
            namedRoutes.KeystoneWarbandRankings,
            buildRouteParams({ ...props.match.params, class: 'all', role: 'all', region: 'world' })
          ),
          isNew: true,
          title: 'nav:mythic-plus.warband-leaderboards',
          wowIcon: 'inv_misc_groupneedmore',
          children: getWarbandSubMenuItems(props)
        },
        {
          type: EMenuEntry.ITEM,
          title: 'nav:mythic-plus.spec-leaderboards',
          wowIcon: 'spell_holy_devineaegis',
          children: getSpecSubMenuItems(props)
        },
        {
          type: EMenuEntry.ITEM,
          link: resolveRoutePath(
            namedRoutes.KeystoneCharacterRankings,
            buildRouteParams({ ...props.match.params, class: 'all', role: 'all' })
          ),
          title: 'nav:mythic-plus.class-and-role-leaderboards',
          wowIcon: 'achievement_guildperk_everyonesahero',
          children: getCharacterSubMenuItems(props)
        },
        {
          type: EMenuEntry.ITEM,
          title: 'nav:mythic-plus.team-leaderboards',
          link: resolveRoutePath(
            namedRoutes.TeamLeaderboardPage,
            buildRouteParams(props.match.params, { week: 'all', bracket: 'all', faction: 'all' })
          ),
          wowIcon: 'achievement_guildperk_everyonesahero_rank2',
          children: [
            {
              type: EMenuEntry.ITEM,
              title: 'nav:mythic-plus.all-brackets',
              link: resolveRoutePath(
                namedRoutes.TeamLeaderboardPage,
                buildRouteParams(props.match.params, { week: 'all', bracket: 'all', faction: 'all' })
              ),
              wowIcon: 'achievement_dungeon_ulduarraid_misc_06'
            },
            {
              type: EMenuEntry.DIVIDER
            },
            ...ALL_KEYSTONE_BRACKETS.map<TMenuEntry>((bracket) => ({
              type: EMenuEntry.ITEM,
              link: resolveRoutePath(
                namedRoutes.TeamLeaderboardPage,
                buildRouteParams(props.match.params, { week: 'all', faction: 'all', bracket: bracket.slug })
              ),
              title: bracket.name,
              wowIcon: bracket.icon
            }))
          ]
        },
        {
          type: EMenuEntry.DIVIDER
        },
        {
          type: EMenuEntry.ITEM,
          title: 'nav:mythic-plus.season-rating-cutoffs',
          link: '/mythic-plus/cutoffs',
          wowIcon: 'ui_embercourt-emoji-happy',
          children: props.regions
            .filter((reg: any) => reg.slug !== 'world')
            .map((reg: any) => ({
              type: EMenuEntry.ITEM,
              title: `regions:${reg.slug}.name`,
              link: resolveRoutePath(
                namedRoutes.MythicPlusCutoffs,
                buildRouteParams({
                  ...props.match.params,
                  season: props.currentSeasonCutoffSlug,
                  region: reg.slug
                })
              )
            }))
        }
        // {
        //   type: EMenuEntry.ITEM,
        //   title: 'nav:mythic-plus.lowest-weekly-keystones',
        //   link: '/mythic-plus/realms',
        //   wowIcon: 'inv_garrison_blueprints3',
        //   children: props.regions
        //     .filter((reg: any) => reg.slug !== 'cn' && reg.slug !== 'world')
        //     .map((reg: any) => ({
        //       type: EMenuEntry.ITEM,
        //       title: `regions:${reg.slug}.name`,
        //       link: `/mythic-plus/realms/${reg.slug}/current`
        //     }))
        // }
      ]
    }
  ];

  const { ownProfiles: recruitmentProfiles } = useRecruitmentProfiles();
  const { resetPagination, specs } = props;

  const { [RecruitmentActivationStatus.Active]: activeProfiles = [] } = _.groupBy(recruitmentProfiles, 'status');

  const buildProfileLink = (p: any, category: string) => {
    let specIcon = {};
    if (p.entity_type === RecruitmentEntityType.Character) {
      const main = _.find(p.characters, { ordinal: 0 })!;
      const mainSpec = getCharacterProfileMainSpec(p, specs);

      specIcon = createElement(SpecIcon, { class: main?.character?.class?.slug, spec: mainSpec?.slug });
    }

    return {
      type: EMenuEntry.ITEM,
      title: getRecruitmentProfileName(p),
      link:
        category === 'active'
          ? buildSearchUrlFromProfile(p)
          : resolveRoutePath(namedRoutes.RecruitmentProfileConfig, { profile_id: p.id }),
      onClick: resetPagination,
      className: getProfileEntityColorClassname(p),

      wowIcon: ACTIVITY_INFO_FOR_TYPE[p.activity_type].icon,
      ...(p.entity_type === RecruitmentEntityType.Character
        ? {
            customIconComponent: specIcon
          }
        : {
            customIcon: asset(`/images/site/${getProfileFaction(p)}_logo_xs.png`)
          })
    };
  };

  const hasRecruitmentProfiles = !_.isEmpty(recruitmentProfiles);
  const hasActiveRecruitmentProfiles = !_.isEmpty(activeProfiles);
  const isLoggedIn = props.currentFeatures.authenticated;

  // TODO: make this menu filled in by actual data from the server on active brackets
  const MDI_CHILD_ITEMS: TMenuEntry[] = [
    {
      type: EMenuEntry.ITEM,
      link: '/events/mdi-midnight-season-1/bracket-leaderboard/time-trials',
      title: t('nav:mdi.time-trials'),
      children: [
        {
          type: EMenuEntry.ITEM,
          link: '/events/mdi-midnight-season-1/bracket-leaderboard/time-trials',
          title: t('nav:mdi.time-trials-leaderboard')
        },
        {
          type: EMenuEntry.ITEM,
          link: '/events/mdi-midnight-season-1/bracket-dungeons/time-trials',
          title: t('nav:mdi.time-trials-dungeons')
        }
      ]
    },
    {
      type: EMenuEntry.ITEM,
      link: '/events/mdi-midnight-season-1/brackets/group-a',
      title: t('nav:mdi.group-stages'),
      children: [
        {
          type: EMenuEntry.ITEM,
          link: '/events/mdi-midnight-season-1/brackets/group-a',
          title: t('nav:mdi.group', { letter: 'A' })
        },
        {
          type: EMenuEntry.ITEM,
          link: '/events/mdi-midnight-season-1/brackets/group-b',
          title: t('nav:mdi.group', { letter: 'B' })
        },
        {
          type: EMenuEntry.ITEM,
          link: '/events/mdi-midnight-season-1/brackets/group-c',
          title: t('nav:mdi.group', { letter: 'C' })
        }
      ]
    },
    {
      type: EMenuEntry.ITEM,
      link: '/events/mdi-midnight-season-1/brackets/global-finals',
      title: t('nav:mdi.global-finals')
    }
  ];

  const TGP_CHILD_ITEMS: TMenuEntry[] = [
    {
      type: EMenuEntry.ITEM,
      link: '/mythic-dungeon-international/the-war-within-season-1-tgp/highlights',
      title: t('nav:events.highlights')
    },
    {
      type: EMenuEntry.DIVIDER
    },
    {
      type: EMenuEntry.ITEM,
      link: '/events/tgp-the-war-within-season-1/info',
      title: 'The Great Push: Details'
    },
    {
      type: EMenuEntry.ITEM,
      link: '/events/tgp-the-war-within-season-1/bracket-leaderboard/time-trials',
      title: t('nav:mdi.time-trials'),
      children: [
        {
          type: EMenuEntry.ITEM,
          link: '/events/tgp-the-war-within-season-1/bracket-leaderboard/time-trials',
          title: 'Time Trials: Leaderboard'
        },
        {
          type: EMenuEntry.ITEM,
          link: '/events/tgp-the-war-within-season-1/bracket-dungeons/time-trials',
          title: 'Time Trials: Dungeons'
        }
      ]
    },
    {
      type: EMenuEntry.ITEM,
      link: '/events/mdi-the-war-within-season-1',
      title: t('nav:mdi.group-stage'),
      children: [
        {
          type: EMenuEntry.ITEM,
          link: '/events/tgp-the-war-within-season-1/the-great-push-leaderboard/group-a-day-one',
          title: 'Group: A',
          children: [
            {
              type: EMenuEntry.ITEM,
              link: '/events/tgp-the-war-within-season-1/the-great-push-leaderboard/group-a-day-one',
              title: 'Day One'
            },
            {
              type: EMenuEntry.ITEM,
              link: '/events/tgp-the-war-within-season-1/the-great-push-leaderboard/group-a-day-two',
              title: 'Day Two'
            },
            {
              type: EMenuEntry.ITEM,
              link: '/events/tgp-the-war-within-season-1/the-great-push-leaderboard/group-a-day-three',
              title: 'Day Three'
            }
          ]
        },
        {
          type: EMenuEntry.ITEM,
          link: '/events/tgp-the-war-within-season-1/the-great-push-leaderboard/group-b-day-one',
          title: 'Group: B',
          children: [
            {
              type: EMenuEntry.ITEM,
              link: '/events/tgp-the-war-within-season-1/the-great-push-leaderboard/group-b-day-one',
              title: 'Day One'
            },
            {
              type: EMenuEntry.ITEM,
              link: '/events/tgp-the-war-within-season-1/the-great-push-leaderboard/group-b-day-two',
              title: 'Day Two'
            },
            {
              type: EMenuEntry.ITEM,
              link: '/events/tgp-the-war-within-season-1/the-great-push-leaderboard/group-b-day-three',
              title: 'Day Three'
            }
          ]
        }
      ]
    },
    {
      type: EMenuEntry.ITEM,
      link: '/events/tgp-the-war-within-season-1/the-great-push-leaderboard/global-finals',
      title: t('nav:mdi.global-finals'),
      children: [
        {
          type: EMenuEntry.ITEM,
          link: '/events/tgp-the-war-within-season-1/the-great-push-leaderboard/global-finals',
          title: 'Leaderboard'
        },
        {
          type: EMenuEntry.ITEM,
          link: '/events/tgp-the-war-within-season-1/the-great-push/global-finals',
          title: 'Dungeons'
        }
      ]
    },

    {
      type: EMenuEntry.DIVIDER
    },
    {
      type: EMenuEntry.ITEM,
      link: '/events/tgp-the-war-within-season-1/prizes',
      title: t('nav:events.prizes')
    }
  ];

  const MDI_ACTIVE_MENU: TMenuEntry[] = [
    {
      type: EMenuEntry.ITEM,
      link: '/tournaments/mdi',
      title: 'nav:main.mdi',
      titleClass: 'text-bold',
      isLive: props.currentFeatures.mdi_live || false,
      children: MDI_CHILD_ITEMS
    }
  ];

  const TGP_ACTIVE_MENU: TMenuEntry[] = [
    {
      type: EMenuEntry.ITEM,
      link: '/tournaments/mdi',
      title: 'nav:main.tgp',
      titleClass: 'text-bold',
      isLive: props.currentFeatures.tgp_live || false,
      children: TGP_CHILD_ITEMS
    }
  ];

  const AWC_CHILD_ITEMS: TMenuEntry[] = [
    {
      type: EMenuEntry.ITEM,
      link: '/events/awc-midnight-season-1-grand-finals/brackets/europe-grand-finals',
      title: t('nav:awc.grand-finals'),
      children: [
        {
          type: EMenuEntry.ITEM,
          link: '/events/awc-midnight-season-1-grand-finals/brackets/europe-grand-finals',
          title: t('nav:awc.grand-finals-bracket', { region: 'Europe' })
        },
        {
          type: EMenuEntry.ITEM,
          link: '/events/awc-midnight-season-1-grand-finals/brackets/north-america-grand-finals',
          title: t('nav:awc.grand-finals-bracket', { region: 'North America' })
        }
      ]
    },
    {
      type: EMenuEntry.ITEM,
      link: '/events/awc-midnight-season-1-grand-finals/brackets/europe-gauntlet',
      title: t('nav:awc.gauntlet'),
      children: [
        {
          type: EMenuEntry.ITEM,
          link: '/events/awc-midnight-season-1-grand-finals/brackets/europe-gauntlet',
          title: t('nav:awc.gauntlet-bracket', { region: 'Europe' })
        },
        {
          type: EMenuEntry.ITEM,
          link: '/events/awc-midnight-season-1-grand-finals/brackets/north-america-gauntlet',
          title: t('nav:awc.gauntlet-bracket', { region: 'North America' })
        }
      ]
    },
    { type: EMenuEntry.DIVIDER },
    {
      type: EMenuEntry.ITEM,
      link: '/events/awc-midnight-season-1-cup-3-europe/brackets/broadcast-bracket',
      title: t('nav:awc.cup', { number: 3 }),
      children: [
        {
          type: EMenuEntry.ITEM,
          link: '/events/awc-midnight-season-1-cup-3-europe/brackets/broadcast-bracket',
          title: t('nav:awc.bracket', { region: 'EU', number: 3 })
        },
        {
          type: EMenuEntry.ITEM,
          link: '/events/awc-midnight-season-1-cup-3-north-america/brackets/broadcast-bracket',
          title: t('nav:awc.bracket', { region: 'NA', number: 3 })
        }
      ]
    },

    {
      type: EMenuEntry.ITEM,
      link: '/events/awc-midnight-season-1-cup-2-europe/brackets/broadcast-bracket',
      title: t('nav:awc.cup', { number: 2 }),
      children: [
        {
          type: EMenuEntry.ITEM,
          link: '/events/awc-midnight-season-1-cup-2-europe/brackets/broadcast-bracket',
          title: t('nav:awc.bracket', { region: 'EU', number: 2 })
        },
        {
          type: EMenuEntry.ITEM,
          link: '/events/awc-midnight-season-1-cup-2-north-america/brackets/broadcast-bracket',
          title: t('nav:awc.bracket', { region: 'NA', number: 2 })
        }
      ]
    },
    {
      type: EMenuEntry.ITEM,
      link: '/events/awc-midnight-season-1-cup-1-europe/brackets/broadcast-bracket',
      title: t('nav:awc.cup', { number: 1 }),
      children: [
        {
          type: EMenuEntry.ITEM,
          link: '/events/awc-midnight-season-1-cup-1-europe/brackets/broadcast-bracket',
          title: t('nav:awc.bracket', { region: 'EU', number: 1 })
        },
        {
          type: EMenuEntry.ITEM,
          link: '/events/awc-midnight-season-1-cup-1-north-america/brackets/broadcast-bracket',
          title: t('nav:awc.bracket', { region: 'NA', number: 1 })
        }
      ]
    },
    { type: EMenuEntry.DIVIDER },
    {
      type: EMenuEntry.ITEM,
      link: '/events/awc-midnight-season-1-cup-1-north-america/prizes',
      title: t('nav:events.prizes')
    },
    { type: EMenuEntry.DIVIDER },

    {
      type: EMenuEntry.ITEM,
      link: '/arena-world-championship',
      wowIcon: 'inv_11xp_awc_banner03',
      title: t('nav:awc.view-all-events')
    }
  ];

  const AWC_ACTIVE_MENU: TMenuEntry[] = [
    {
      type: EMenuEntry.ITEM,
      link: '/arena-world-championship',
      title: 'nav:main.awc',
      titleClass: 'text-bold',
      isLive: props.currentFeatures.awc_live || false,
      children: AWC_CHILD_ITEMS
    }
  ];

  const RECRUITMENT_MENU = props.currentFeatures.recruitment_view
    ? [
        {
          type: EMenuEntry.ITEM,
          title: 'nav:main.recruitment',
          titleClass: 'text-bold',
          link: isLoggedIn
            ? resolveRoutePath(namedRoutes.UserRecruitmentProfiles)
            : resolveRoutePath(namedRoutes.Recruitment),
          // isBeta: true,
          children: isLoggedIn
            ? () => [
                ...(!hasRecruitmentProfiles
                  ? [
                      {
                        type: EMenuEntry.ITEM,
                        title: 'nav:recruitment.create-profile',
                        link: resolveRoutePath(namedRoutes.RecruitmentProfileCreate)
                      }
                    ]
                  : []),
                ...(hasRecruitmentProfiles
                  ? [
                      {
                        type: EMenuEntry.ITEM,
                        title: 'nav:recruitment.view-profiles',
                        link: resolveRoutePath(namedRoutes.UserRecruitmentProfiles)
                      }
                    ]
                  : []),
                ...(hasActiveRecruitmentProfiles
                  ? [{ type: EMenuEntry.DIVIDER }, ..._.map(activeProfiles, (p) => buildProfileLink(p, 'active'))]
                  : []),
                { type: EMenuEntry.DIVIDER },
                {
                  type: EMenuEntry.ITEM,
                  title: 'nav:recruitment.browse-guilds',
                  link: recruitmentSearchPaths.guilds
                },
                {
                  type: EMenuEntry.ITEM,
                  title: 'nav:recruitment.browse-players',
                  link: recruitmentSearchPaths.players
                },
                {
                  type: EMenuEntry.ITEM,
                  title: 'nav:recruitment.browse-teams',
                  link: recruitmentSearchPaths.teams
                },
                { type: EMenuEntry.DIVIDER },
                {
                  type: EMenuEntry.ITEM,
                  title: 'nav:recruitment.about-recruitment',
                  className: 'text-raiderio text-bold',
                  link: resolveRoutePath(namedRoutes.Recruitment)
                }
              ]
            : [
                {
                  type: EMenuEntry.ITEM,
                  title: 'nav:recruitment.browse-guilds',
                  link: `/search?type=guild&mplus.${currentSeason.slug}.top20[0][gte]=&recruitment.guild_raids.languages[0][eq]=&recruitment.guild_raids.tags[0][eq]=&recruitment.guild_raids.schedule[0][eq][endTime]=&recruitment.guild_raids.schedule[0][eq][day]=&recruitment.guild_raids.profile.published_at[0][gte]=&sort[recruitment.guild_raids.profile.published_at]=desc`
                },
                {
                  type: EMenuEntry.ITEM,
                  title: 'nav:recruitment.browse-players',
                  link: '/search?type=character&recruitment.guild_raids.main_character.main_roles[0][eq]=tank&recruitment.guild_raids.main_character.mainspec_ids[0][eq]=&recruitment.guild_raids.main_character.offspec_ids[0][eq]=&recruitment.guild_raids.schedule[0][eq][day]=&recruitment.guild_raids.schedule[0][eq][startTime]=&recruitment.guild_raids.schedule[0][eq][endTime]=&recruitment.guild_raids.profile.published_at[0][gte]=&sort[recruitment.guild_raids.profile.published_at]=desc'
                },
                {
                  type: EMenuEntry.ITEM,
                  title: 'nav:recruitment.browse-teams',
                  link: `/search?type=team&mplus.${currentSeason.slug}.top20[0][gte]=&recruitment.team_mplus.languages[0][eq]=&recruitment.team_mplus.tags[0][eq]=&recruitment.team_mplus.schedule[0][eq][endTime]=&recruitment.team_mplus.schedule[0][eq][day]=&recruitment.team_mplus.profile.published_at[0][gte]=&sort[recruitment.team_mplus.published_at]=desc`
                },
                {
                  type: EMenuEntry.DIVIDER
                },
                {
                  type: EMenuEntry.ITEM,
                  title: 'nav:recruitment.about-recruitment',
                  className: 'text-bold text-raiderio',
                  link: resolveRoutePath(namedRoutes.Recruitment)
                }
              ]
        }
      ]
    : [];

  const TALENT_BUILDS_MENU = props.currentFeatures[FeatureFlag.TALENT_BUILDS_NAV]
    ? [
        {
          type: EMenuEntry.ITEM,
          title: 'nav:main.talent-builds',
          titleClass: 'text-bold',
          link: resolveRoutePath(namedRoutes.SpecsIndex),
          isNew: true
        }
      ]
    : [];

  const isCommunityEventsActive = true;
  const isEventInsightsActive = true;
  const isEventStandingsActive = true;

  const EVENTS_MENU = [
    {
      type: EMenuEntry.ITEM,
      link: resolveRoutePath(namedRoutes.TournamentsHub, {}),
      title: 'nav:main.events',
      titleClass: 'text-blizzard text-bold',
      PreIcon: renderTournamentsIcon,
      children: [
        {
          type: EMenuEntry.ITEM,
          link: resolveRoutePath(namedRoutes.TournamentsHub, {}),
          title: 'nav:events.blizzard-tournaments',
          titleClass: 'text-blizzard text-bold',
          wowIcon: 'ability_maldraxxus_warriorplantbanner'
        },

        {
          type: EMenuEntry.ITEM,
          link: '/tournaments/mdi',
          title: 'nav:events.mdi',
          titleClass: 'text-blizzard text-bold',
          wowIcon: 'inv_misc_mdi_banner02'
          // children: [
          //   ...TGP_CHILD_ITEMS,
          //   { type: EMenuEntry.DIVIDER },

          //   {
          //     type: EMenuEntry.ITEM,
          //     link: '/mythic-dungeon-international',
          //     wowIcon: 'inv_misc_mdi_banner01',
          //     title: 'View All MDI Events'
          //   }
          // ]
        },

        {
          type: EMenuEntry.ITEM,
          link: '/tournaments/awc',
          title: 'nav:events.awc',
          titleClass: 'text-blizzard text-bold',
          wowIcon: 'inv_11xp_awc_banner02'
          // children: AWC_CHILD_ITEMS
        },

        {
          type: EMenuEntry.DIVIDER
        },

        ...(isEventInsightsActive
          ? [
              {
                type: EMenuEntry.ITEM,
                link: namedRoutes.EventInsights,
                title: 'nav:events.event-insights',
                wowIcon: 'achievement_bg_win_av_x_times'
              }
            ]
          : []),
        ...(isEventStandingsActive
          ? [
              {
                type: EMenuEntry.ITEM,
                link: namedRoutes.EventStandings,
                title: 'nav:events.event-standings',
                wowIcon: 'trade_archaeology_chaliceofmountainkings'
              }
            ]
          : []),

        ...(isEventStandingsActive || isEventInsightsActive
          ? [
              {
                type: EMenuEntry.DIVIDER
              }
            ]
          : []),

        ...(isCommunityEventsActive
          ? [
              {
                type: EMenuEntry.ITEM,
                link: resolveRoutePath(namedRoutes.CommunityEvents, {}),
                title: 'nav:events.community-events',
                wowIcon: 'achievement_guildperk_everybodysfriend'
              }
            ]
          : []),

        {
          type: EMenuEntry.ITEM,
          title: 'nav:events.older-events',
          wowIcon: 'inv_toy_booklibrary',
          children: [
            {
              type: EMenuEntry.ITEM,
              link: '/mythic-dungeon-international',
              title: 'nav:mdi.mythic-dungeon-international',
              wowIcon: 'inv_misc_mdi_banner02',
              children: MDI_EVENT_MENU_CHILDREN
            },
            {
              type: EMenuEntry.ITEM,
              title: 'nav:mythic-plus.community-events',
              wowIcon: 'spell_holy_rebuke',
              children: [
                {
                  type: EMenuEntry.ITEM,
                  title: t('nav:mythic-plus.events.break-the-meta.title', {
                    expansionAbreviation: t('common:expansions.dragonflight.short-name'),
                    seasonNumber: t('common:season-x', { num: 2 })
                  }),
                  link: '/mythic-plus-rankings/season-df-2-break-the-meta/all/world/leaderboards-strict',
                  children: [
                    {
                      type: EMenuEntry.ITEM,
                      title: t('nav:mythic-plus.events.break-the-meta.menu-item-children.runs-leaderboard'),
                      link: '/mythic-plus-rankings/season-df-2-break-the-meta/all/world/leaderboards-strict',
                      wowIcon: 'spell_holy_borrowedtime'
                    },
                    {
                      type: EMenuEntry.DIVIDER
                    },
                    {
                      type: EMenuEntry.ITEM,
                      title: t('nav:mythic-plus.events.break-the-meta.menu-item-children.dps-leaderboards'),
                      link: '/mythic-plus-character-rankings/season-df-2-break-the-meta/world/all/dps',
                      iconClassName: 'icon-role icon-x-small dps-lfg',
                      children: [
                        ['affliction', 'warlock'],
                        ['arcane', 'mage'],
                        ['arms', 'warrior'],
                        ['assassination', 'rogue'],
                        ['demonology', 'warlock'],
                        ['devastation', 'evoker'],
                        ['elemental', 'shaman'],
                        ['feral', 'druid'],
                        ['frost', 'death-knight'],
                        ['havoc', 'demon-hunter'],
                        ['marksmanship', 'hunter'],
                        ['outlaw', 'rogue'],
                        ['survival', 'hunter'],
                        ['unholy', 'death-knight']
                      ].map(([specSlug, classSlug]: string[]) => ({
                        type: EMenuEntry.ITEM,
                        link: `/mythic-plus-spec-rankings/season-df-2-break-the-meta/world/${classSlug}/${specSlug}`,
                        customIconComponent: createElement(SpecIcon, { class: classSlug, spec: specSlug }),
                        className: `class-color--${classSlug}`,
                        title: `${t(`game:specs.${classSlug}.${specSlug}.name`)} ${t(`game:classes.${classSlug}.name`)}`
                      }))
                    },
                    {
                      type: EMenuEntry.ITEM,
                      title: t('nav:mythic-plus.events.break-the-meta.menu-item-children.healer-leaderboards'),
                      link: '/mythic-plus-character-rankings/season-df-2-break-the-meta/world/all/healer',
                      iconClassName: 'icon-role icon-x-small healer-lfg',
                      children: [
                        ['holy', 'priest'],
                        ['mistweaver', 'monk'],
                        ['preservation', 'evoker']
                      ].map(([specSlug, classSlug]: string[]) => ({
                        type: EMenuEntry.ITEM,
                        link: `/mythic-plus-spec-rankings/season-df-2-break-the-meta/world/${classSlug}/${specSlug}`,
                        customIconComponent: createElement(SpecIcon, { class: classSlug, spec: specSlug }),
                        className: `class-color--${classSlug}`,
                        title: `${t(`game:specs.${classSlug}.${specSlug}.name`)} ${t(`game:classes.${classSlug}.name`)}`
                      }))
                    },
                    {
                      type: EMenuEntry.ITEM,
                      title: t('nav:mythic-plus.events.break-the-meta.menu-item-children.tank-leaderboards'),
                      link: '/mythic-plus-character-rankings/season-df-2-break-the-meta/world/all/tank',
                      iconClassName: 'icon-role icon-x-small tank-lfg',
                      children: [
                        ['blood', 'death-knight'],
                        ['brewmaster', 'monk'],
                        ['protection', 'paladin'],
                        ['protection', 'warrior']
                      ].map(([specSlug, classSlug]: string[]) => ({
                        type: EMenuEntry.ITEM,
                        link: `/mythic-plus-spec-rankings/season-df-2-break-the-meta/world/${classSlug}/${specSlug}`,
                        customIconComponent: createElement(SpecIcon, { class: classSlug, spec: specSlug }),
                        className: `class-color--${classSlug}`,
                        title: `${t(`game:specs.${classSlug}.${specSlug}.name`)} ${t(`game:classes.${classSlug}.name`)}`
                      }))
                    },
                    {
                      type: EMenuEntry.DIVIDER
                    },
                    {
                      type: EMenuEntry.ITEM,
                      title: t('nav:mythic-plus.events.break-the-meta.menu-item-children.announcement'),
                      link: '/news/558-break-the-meta-dragonflight-season-2',
                      wowIcon: 'inv_misc_horn_04'
                    }
                  ]
                },
                {
                  type: EMenuEntry.ITEM,
                  title: t('nav:mythic-plus.events.eve-ascension.eve-ascesnion-series', {
                    number: 3
                  }),
                  children: [
                    {
                      type: EMenuEntry.ITEM,
                      title: t('nav:mythic-plus.events.eve-ascension.eve-ascension-series-teams', {
                        number: 3,
                        series: t('nav:mythic-plus.Tournament')
                      }),
                      link: '/eve-ascension-df-season-2/teams/global-finals',
                      children: [
                        {
                          type: EMenuEntry.ITEM,
                          title: 'nav:mdi.tournament-teams',
                          link: '/eve-ascension-df-season-2/teams/global-finals'
                        },
                        {
                          type: EMenuEntry.ITEM,
                          title: 'nav:mdi.tournament-dungeons',
                          link: '/eve-ascension-df-season-2/maps/global-finals'
                        }
                      ]
                    },
                    {
                      type: EMenuEntry.ITEM,
                      title: t('nav:mythic-plus.events.eve-ascension.eve-ascension-series-teams', {
                        number: 3,
                        series: t('nav:mythic-plus.qualifiers')
                      }),
                      link: '/eve-ascension-df-season-2/teams',
                      children: [
                        {
                          type: EMenuEntry.ITEM,
                          title: 'nav:mdi.team-leaderboards',
                          link: '/team-rankings/season-df-2/world/10087'
                        },
                        {
                          type: EMenuEntry.ITEM,
                          title: 'nav:mdi.dungeon-leaderboards',
                          link: '/mythic-plus-rankings/season-df-2-event-eve-ascension/all/world/leaderboards'
                        }
                      ]
                    }
                  ]
                },
                {
                  type: EMenuEntry.ITEM,
                  title: 'nav:mythic-plus.ukraine-event',
                  link: resolveRoutePath(namedRoutes.UKraineMPlusDFSeason2, {
                    noRealm: true,
                    bracket: 'finals'
                  })
                },

                ...DF_SEASON_1_COMMUNITY_EVENTS,

                ...getCommunityEventMenuItems()
              ]
            }
          ]
        }
      ]
    }
  ];

  const { groupVisibilityMap, visibilityMap } = parseMetricsVisibility(
    props.currentFeatures?.params?.[EXPORT_METRICS_VISIBILITY_PARAM_KEY]
  );

  const getStatisticsMenuItems = () => {
    const createMenuItem = (metricType: StatisticsDataType, additionalProps?: Record<string, any>) => {
      const icon = STATISTIC_METRICS_ICONS[metricType];

      const getPath = () => {
        const isSpecStatisticsMetric =
          metricType === StatisticsDataType.TierTalents ||
          metricType === StatisticsDataType.TierGear ||
          metricType === StatisticsDataType.TierTrinkets;

        // TODO: add mapper
        if (isSpecStatisticsMetric) {
          return `/specs/${metricType}`;
        }

        const route = STATISTIC_TYPE_TO_ROUTE_TYPE_MAP[metricType];

        return `/stats/${route}`;
      };

      return {
        type: EMenuEntry.ITEM,
        title: (
          <>
            {t(`statistics:${metricType}.title`)}
            {BETA_STATISTICS_TYPES[metricType] && (
              <span className="slds-m-left--x-small new-feature-indicator bottom-0">BETA</span>
            )}
          </>
        ),
        titleClass: 'text-bold',
        link: getPath(),
        ...(icon ? { iconClassName: `fa fa-fw ${icon}` } : {}),
        ...additionalProps
      };
    };

    const itemsByGroups = Object.entries(groupVisibilityMap)
      .filter(([_, isVisible]) => isVisible)
      .reduce<Partial<Record<StatisticMetricsMenuItemsGroup, TMenuEntryItem[]>>>((acc, [group]) => {
        const menuItems = STATISTICS_GROUPS_TO_DATA_TYPES_MAP[group as StatisticMetricsMenuItemsGroup]
          .filter((metricType) => visibilityMap[metricType])
          .map((metricType) => createMenuItem(metricType)) as TMenuEntryItem[];

        if (menuItems.length > 0) {
          acc[group as StatisticMetricsMenuItemsGroup] = menuItems;
        }

        return acc;
      }, {});

    const visibleGroups = Object.keys(itemsByGroups) as StatisticMetricsMenuItemsGroup[];

    if (visibleGroups.length > 1) {
      return visibleGroups.map((group) => ({
        type: EMenuEntry.ITEM,
        title: (
          <>
            {t(`statistics:statistic-groups.${group}`)}
            {BETA_STATISTICS_GROUPS[group] && (
              <span className="slds-m-left--x-small new-feature-indicator bottom-0">BETA</span>
            )}
          </>
        ),
        titleClass: 'text-bold',
        customIcon: asset(`/images/wow/icons/large/${STATISTIC_GROUPS_METRIC_ICON_SRC_MAP[group]}.jpg`),
        link: itemsByGroups[group]?.[0]?.link,
        children: itemsByGroups[group]
      }));
    }

    const anyItems = Object.values(itemsByGroups).filter(Boolean)[0];

    return anyItems ? anyItems : [];
  };

  const STATISTICS_MENU = [
    {
      type: EMenuEntry.ITEM,
      title: 'nav:main.statistics',
      link: resolveRoutePath(namedRoutes.Statistics, {}),
      titleClass: 'text-bold',
      // isNew: true,
      children: [
        ...getStatisticsMenuItems(),
        {
          type: EMenuEntry.DIVIDER
        },
        {
          type: EMenuEntry.ITEM,
          title: 'nav:main.realms',
          titleClass: 'text-bold',
          link: '/realms',
          children: getRealmsMenuItems(props),
          iconClassName: 'fa fa-earth-americas'
        }
      ]
    }
  ];

  return [
    ...RAID_MENU,
    ...MYTHIC_PLUS_MENU,
    ...RECRUITMENT_MENU,
    ...TALENT_BUILDS_MENU,
    ...STATISTICS_MENU,
    ...EVENTS_MENU,
    ...(props.currentFeatures.rwf_live ? RWF_ACTIVE_MENU : []),
    ...(props.currentFeatures.mdi_live ? MDI_ACTIVE_MENU : []),
    ...(props.currentFeatures.tgp_live ? TGP_ACTIVE_MENU : []),
    ...(props.currentFeatures.awc_live ? AWC_ACTIVE_MENU : []),
    {
      type: EMenuEntry.ITEM,
      title: 'nav:community.discord-alerts',
      titleClass: 'text-bold',
      link: '/discord'
    },

    // {
    //   type: EMenuEntry.ITEM,
    //   title: 'nav:community.stream-widgets',
    //   titleClass: 'text-bold',
    //   link: namedRoutes.Widget
    // },

    {
      type: EMenuEntry.ITEM,
      title: 'nav:main.extras',
      titleClass: 'text-bold',
      children: getExtrasMenus(props)
    },
    ...(props.currentFeatures.new_season_recap
      ? [
          {
            type: EMenuEntry.ITEM,
            title: 'nav:main.new-season-recap',
            titleClass: 'text-bold text-raiderio',
            isNew: true,
            key: 'new-season-recap',
            link: '/tww-review'
          }
        ]
      : [])

    // {
    //   type: EMenuEntry.ITEM,
    //   link: '/addon',
    //   title: 'nav:main.addon',
    //   titleClass: 'text-bold',
    //   postIconClass: 'fas fa-download slds-m-left--x-small slds-text-body--small text-raiderio'
    // }
  ] as TMenuEntry[];
}

function renderTournamentsIcon() {
  return (
    <img
      className="blizzard-menu-icon"
      src={asset('/images/blizzard_logo.svg')}
      style={{
        position: 'relative',
        top: -2,
        marginRight: '0.25rem'
      }}
    />
  );
}
